import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Modal,
  Offcanvas,
} from "react-bootstrap";
import ToastNotification from "../ToastNotification";
import { cpf, cnpj } from "cpf-cnpj-validator";
import { API_URL } from "../../apiConfig";
import { formatPhone } from "../../utils/phone";
import ActivationDetails from "./ActivationDetails";
import { useLocation, useNavigate } from "react-router-dom";
import { EyeIcon, PencilSquareIcon, TrashIcon, XCircleIcon, CheckCircleIcon, MagnifyingGlassIcon, Cog6ToothIcon, WifiIcon, PlusIcon, XMarkIcon, ArrowPathIcon, MapPinIcon, ChevronLeftIcon, ChevronRightIcon, ChatBubbleLeftRightIcon, ArrowUturnLeftIcon, PaperAirplaneIcon, RocketLaunchIcon, ArrowRightIcon, BuildingOffice2Icon, DocumentDuplicateIcon, ClockIcon, InboxIcon, ArchiveBoxIcon, Squares2X2Icon } from "@heroicons/react/24/outline";

// ── Design tokens ──────────────────────────────────────────────────────
const T = {
  bg:         "#f2f1ee",
  surface:    "rgba(255,255,255,0.72)",
  surface2:   "#f9f8f6",
  ink:        "#1A1A1C",
  ink2:       "#4A4A50",
  ink3:       "#86868B",
  ink4:       "#B4B3B0",
  hair:       "rgba(20,20,22,0.07)",
  hair2:      "rgba(20,20,22,0.04)",
  accent:     "#1e3a8a",
  accentSoft: "rgba(30,58,138,0.09)",
  ok:         "#1E8E3E",
  okSoft:     "rgba(30,142,62,0.10)",
  warn:       "#B25E09",
  warnSoft:   "rgba(178,94,9,0.10)",
  danger:     "#C5221F",
  dangerSoft: "rgba(197,34,31,0.10)",
  shadowSm:   "0 1px 2px rgba(20,20,22,0.04),0 1px 1px rgba(20,20,22,0.03)",
  shadowMd:   "0 6px 24px -8px rgba(20,20,22,0.08),0 2px 6px rgba(20,20,22,0.04)",
  font:       "-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
  r:          18,
};
const cardStyle      = { background: T.surface, backdropFilter: "blur(28px) saturate(180%)", WebkitBackdropFilter: "blur(28px) saturate(180%)", border: `1px solid ${T.hair}`, borderRadius: T.r, boxShadow: T.shadowMd };
const cardHeadStyle  = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: `1px solid ${T.hair}` };
const cardTitleStyle = { fontSize: 13, fontWeight: 600, letterSpacing: "-0.005em", color: T.ink, margin: 0, display: "flex", alignItems: "center", gap: 8 };
const inputStyle     = { borderRadius: 10, borderColor: T.hair, fontSize: 13, fontFamily: T.font };

const AVATAR_PALETTE = ["#1e3a8a", "#0f5c2e", "#7c2d12", "#4c1d95", "#b45309"];
function avatarColor(name = "") {
  const n = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_PALETTE[n % AVATAR_PALETTE.length];
}
function initials(name = "") {
  const p = (name || "").trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : (name || "?").slice(0, 2).toUpperCase();
}

function ProviderAutocomplete({ value, onChange, options, style, placeholder, required }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const wrapRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    function onDown(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const filtered = options.filter(o => o.toLowerCase().includes((query || "").toLowerCase()));

  function select(name) {
    onChange({ target: { value: name } });
    setQuery(name);
    setOpen(false);
  }

  function handleInput(e) {
    const v = e.target.value;
    setQuery(v);
    onChange({ target: { value: v } });
    setOpen(true);
  }

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        value={query}
        onChange={handleInput}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || "Digite ou selecione\u2026"}
        required={required}
        style={{ ...style, width: "100%", display: "block" }}
      />
      {open && filtered.length > 0 && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 9999, background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, boxShadow: "0 8px 24px -4px rgba(20,20,22,0.14)", maxHeight: 180, overflowY: "auto" }}>
          {filtered.map(name => (
            <div
              key={name}
              onMouseDown={() => select(name)}
              style={{ padding: "9px 14px", fontSize: 13, cursor: "pointer", color: name === value ? T.accent : T.ink, background: name === value ? T.accentSoft : "transparent", fontWeight: name === value ? 500 : 400, borderBottom: `1px solid ${T.hair2}` }}
              onMouseEnter={e => { e.currentTarget.style.background = T.surface2; }}
              onMouseLeave={e => { e.currentTarget.style.background = name === value ? T.accentSoft : "transparent"; }}
            >
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ act }) {
  if (act.disabledAt) return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.surface2, color:T.ink3 }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:T.ink3, display:"inline-block" }}/>Inativado
    </span>
  );
  const s = String(act.status || "PENDENTE").toUpperCase();
  if (s === "ATIVADO") return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.okSoft, color:T.ok }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:T.ok, display:"inline-block" }}/>Ativado
    </span>
  );
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.warnSoft, color:T.warn }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:T.warn, display:"inline-block" }}/>Pendente
    </span>
  );
}

const normName = (s = "") => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]/g, "");

// Encontra a configuração de provedores do condomínio (por ID da Superlógica, com fallback pelo nome)
function findCondoProviderConfig(configs, prop) {
  if (!configs?.length || !prop) return null;
  if (prop.condoId) {
    const byId = configs.find((c) => String(c.condoId) === String(prop.condoId));
    if (byId) return byId;
  }
  const n = normName(prop.condoName);
  return n ? configs.find((c) => normName(c.condoName) === n) || null : null;
}

function InternetTags({ catalog, config }) {
  if (!config) return <span style={{ fontSize: 12, color: T.ink4 }}>Não configurado</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {catalog.map(({ id, name, color }) => {
        const available = config.partnerIds.includes(id);
        // Cor do parceiro (hex) quando cadastrada; senão, verde padrão
        const hex = /^#[0-9a-f]{6}$/i.test(color || "") ? color : null;
        const fg = hex ? "#fff" : T.ok;
        const bg = hex || T.okSoft;
        return (
          <span
            key={id}
            title={available ? `${name} disponível` : `${name} indisponível`}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px", borderRadius: 999, fontSize: 11.5, fontWeight: 500, whiteSpace: "nowrap", background: available ? bg : T.surface2, color: available ? fg : T.ink4, textDecoration: available ? "none" : "line-through", border: `1px solid ${available ? (hex || "transparent") : T.hair}` }}
          >
            {available && <CheckCircleIcon style={{ width: 12, height: 12 }} />}
            {name}
          </span>
        );
      })}
    </div>
  );
}

const CONDOS_PER_PAGE = 50;

function mapsUrl(c) {
  const query = [c.name, c.address, c.city && c.uf ? `${c.city}/${c.uf}` : c.city, c.cep].filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function CondoProvidersModal({ show, onHide, onOpen, token, catalog, partners, onCatalogChange, onConfigChange, notify }) {
  const [condos, setCondos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);
  const [newPartnerId, setNewPartnerId] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setErr("");
    try {
      const r = await fetch(`${API_URL}/admin/condo-providers/condos${refresh ? "?refresh=1" : ""}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao carregar condomínios");
      setCondos(data.condos || []);
    } catch (e) {
      setErr(e?.message || "Falha ao carregar condomínios");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (show) { load(); onOpen(); } }, [show, load, onOpen]);

  const visible = useMemo(() => {
    const q = normName(filter);
    return condos.filter((c) => (!onlyPending || !c.configured) && (!q || normName(`${c.name} ${c.address || ""} ${c.city || ""}`).includes(q)));
  }, [condos, filter, onlyPending]);

  useEffect(() => { setPage(1); }, [filter, onlyPending]);

  const totalPages = Math.max(1, Math.ceil(visible.length / CONDOS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = visible.slice((currentPage - 1) * CONDOS_PER_PAGE, currentPage * CONDOS_PER_PAGE);

  const configuredCount = condos.filter((c) => c.configured).length;

  async function saveCondo(condo, partnerIds) {
    const prev = condos;
    setCondos((list) => list.map((c) => (c.id === condo.id ? { ...c, configured: true, partnerIds } : c)));
    setSavingId(condo.id);
    try {
      const r = await fetch(`${API_URL}/admin/condo-providers/${encodeURIComponent(condo.id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ condoName: condo.name, partnerIds }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao salvar");
      onConfigChange({ condoId: condo.id, condoName: condo.name, partnerIds: data.config?.partnerIds || partnerIds });
    } catch (e) {
      setCondos(prev);
      notify("Erro", e?.message || "Falha ao salvar provedores", "danger");
    } finally {
      setSavingId(null);
    }
  }

  async function resetCondo(condo) {
    const prev = condos;
    setCondos((list) => list.map((c) => (c.id === condo.id ? { ...c, configured: false, partnerIds: [] } : c)));
    try {
      const r = await fetch(`${API_URL}/admin/condo-providers/${encodeURIComponent(condo.id)}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Falha ao remover configuração");
      onConfigChange({ condoId: condo.id, removed: true });
    } catch (e) {
      setCondos(prev);
      notify("Erro", e?.message || "Falha ao remover configuração", "danger");
    }
  }

  function toggleProvider(condo, partnerId) {
    const has = condo.partnerIds.includes(partnerId);
    saveCondo(condo, has ? condo.partnerIds.filter((id) => id !== partnerId) : [...condo.partnerIds, partnerId]);
  }

  async function saveCatalog(nextIds) {
    try {
      const r = await fetch(`${API_URL}/admin/provider-catalog`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ partnerIds: nextIds }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao salvar lista");
      onCatalogChange(data.providers);
    } catch (e) {
      notify("Erro", e?.message || "Falha ao salvar lista de provedores", "danger");
    }
  }

  const availablePartners = partners.filter((p) => !catalog.some((c) => c.id === p.id));

  function addProvider(e) {
    e.preventDefault();
    if (!newPartnerId) return;
    saveCatalog([...catalog.map((c) => c.id), newPartnerId]);
    setNewPartnerId("");
  }

  function removeProvider(provider) {
    if (!window.confirm(`Remover "${provider.name}" da lista de provedores?`)) return;
    saveCatalog(catalog.filter((c) => c.id !== provider.id).map((c) => c.id));
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header closeButton style={{ borderBottom: `1px solid ${T.hair}`, padding: "18px 24px" }}>
        <Modal.Title style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: T.accentSoft, color: T.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <WifiIcon style={{ width: 18, height: 18 }} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em", color: T.ink }}>Internet por condomínio</div>
            <div style={{ fontSize: 12, color: T.ink3, marginTop: 1 }}>Marque os provedores disponíveis em cada condomínio</div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "18px 24px", fontFamily: T.font }}>
        {/* Catálogo de provedores */}
        <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Provedores</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 18 }}>
          {catalog.map((provider) => (
            <span key={provider.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 6px 4px 12px", borderRadius: 999, background: T.accentSoft, color: T.accent, fontSize: 12.5, fontWeight: 500 }}>
              {provider.name}
              <button type="button" onClick={() => removeProvider(provider)} title="Remover" style={{ border: "none", background: "transparent", color: T.accent, padding: 2, display: "flex", cursor: "pointer" }}>
                <XMarkIcon style={{ width: 13, height: 13 }} />
              </button>
            </span>
          ))}
          <form onSubmit={addProvider} style={{ display: "inline-flex", gap: 4 }}>
            <Form.Select value={newPartnerId} onChange={(e) => setNewPartnerId(e.target.value)} disabled={!availablePartners.length} style={{ ...inputStyle, height: 30, padding: "0 30px 0 10px", width: 200 }}>
              <option value="">{availablePartners.length ? "Selecionar parceiro…" : "Todos os parceiros adicionados"}</option>
              {availablePartners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Form.Select>
            <button type="submit" disabled={!newPartnerId} style={{ display: "inline-flex", alignItems: "center", gap: 4, height: 30, padding: "0 10px", borderRadius: 10, border: "none", background: T.accent, color: "#fff", fontSize: 12.5, cursor: "pointer", opacity: newPartnerId ? 1 : 0.5 }}>
              <PlusIcon style={{ width: 13, height: 13 }} /> Adicionar
            </button>
          </form>
        </div>
        {!catalog.length && (
          <div style={{ marginTop: -10, marginBottom: 16, fontSize: 12, color: T.warn }}>Selecione os parceiros que são provedores de internet para começar.</div>
        )}

        {/* Filtro */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
            <MagnifyingGlassIcon style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", width: 15, height: 15, color: T.ink3, pointerEvents: "none" }} />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Buscar condomínio, endereço ou cidade…" style={{ ...inputStyle, border: `1px solid ${T.hair}`, width: "100%", height: 36, padding: "0 12px 0 34px", outline: "none", background: T.surface2 }} />
          </div>
          <Form.Check type="switch" id="cp-only-pending" label="Só não configurados" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} style={{ fontSize: 13, color: T.ink2 }} />
          <button type="button" onClick={() => load(true)} disabled={loading} title="Recarregar lista da Superlógica" style={{ border: `1px solid ${T.hair}`, background: "transparent", borderRadius: 10, height: 36, width: 36, display: "flex", alignItems: "center", justifyContent: "center", color: T.ink2, cursor: "pointer" }}>
            <ArrowPathIcon style={{ width: 16, height: 16 }} />
          </button>
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginBottom: 8 }}>
          {configuredCount} de {condos.length} condomínios configurados
        </div>

        {err && <div style={{ marginBottom: 10, padding: "10px 14px", borderRadius: 10, background: T.dangerSoft, color: T.danger, fontSize: 13 }}>{err}</div>}

        {loading && !condos.length ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner animation="border" size="sm" /> <span style={{ fontSize: 13, color: T.ink3, marginLeft: 8 }}>Carregando condomínios da Superlógica…</span></div>
        ) : (
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 12, overflow: "hidden" }}>
            {pageItems.map((c) => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 14px", borderBottom: `1px solid ${T.hair2}`, background: c.configured ? "transparent" : "rgba(178,94,9,0.03)" }}>
                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>{c.name}</div>
                  <a
                    href={mapsUrl(c)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Abrir no Google Maps"
                    style={{ display: "inline-flex", alignItems: "flex-start", gap: 4, fontSize: 11.5, color: T.accent, textDecoration: "none", marginTop: 1 }}
                  >
                    <MapPinIcon style={{ width: 12, height: 12, flexShrink: 0, marginTop: 2 }} />
                    <span>{c.address || "Ver no mapa"}{c.cep ? ` · CEP ${c.cep}` : ""}</span>
                  </a>
                  <div style={{ fontSize: 11.5, color: T.ink3 }}>
                    {c.city || "—"}{c.uf ? `/${c.uf}` : ""}
                    {!c.configured && <span style={{ color: T.warn, marginLeft: 8 }}>· não configurado</span>}
                    {c.configured && !c.partnerIds.length && <span style={{ color: T.danger, marginLeft: 8 }}>· nenhum disponível</span>}
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
                  {catalog.map(({ id, name }) => {
                    const active = c.partnerIds.includes(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => toggleProvider(c, id)}
                        disabled={savingId === c.id}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4, border: `1px solid ${active ? T.ok : T.hair}`, background: active ? T.okSoft : "transparent", color: active ? T.ok : T.ink3, borderRadius: 999, padding: "3px 11px", fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer", fontFamily: T.font, transition: "all .12s" }}
                      >
                        {active && <CheckCircleIcon style={{ width: 12, height: 12 }} />}
                        {name}
                      </button>
                    );
                  })}
                  {c.configured && (
                    <button type="button" onClick={() => resetCondo(c)} title="Marcar como não configurado" style={{ border: "none", background: "transparent", color: T.ink4, padding: 2, display: "flex", cursor: "pointer" }}>
                      <XMarkIcon style={{ width: 14, height: 14 }} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!visible.length && !loading && <div style={{ padding: 24, fontSize: 13, color: T.ink4, textAlign: "center" }}>Nenhum condomínio encontrado.</div>}
          </div>
        )}

        {totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 12, fontSize: 12.5, color: T.ink3 }}>
            <span>
              {(currentPage - 1) * CONDOS_PER_PAGE + 1}–{Math.min(currentPage * CONDOS_PER_PAGE, visible.length)} de {visible.length}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button type="button" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)} style={{ display: "flex", alignItems: "center", gap: 4, height: 30, padding: "0 10px", borderRadius: 10, border: `1px solid ${T.hair}`, background: "transparent", color: T.ink2, fontSize: 12.5, cursor: currentPage <= 1 ? "not-allowed" : "pointer", opacity: currentPage <= 1 ? 0.4 : 1 }}>
                <ChevronLeftIcon style={{ width: 13, height: 13 }} /> Anterior
              </button>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>Página {currentPage} de {totalPages}</span>
              <button type="button" disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)} style={{ display: "flex", alignItems: "center", gap: 4, height: 30, padding: "0 10px", borderRadius: 10, border: `1px solid ${T.hair}`, background: "transparent", color: T.ink2, fontSize: 12.5, cursor: currentPage >= totalPages ? "not-allowed" : "pointer", opacity: currentPage >= totalPages ? 0.4 : 1 }}>
                Próxima <ChevronRightIcon style={{ width: 13, height: 13 }} />
              </button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ borderTop: `1px solid ${T.hair}`, padding: "12px 24px" }}>
        <span style={{ fontSize: 12, color: T.ink3, marginRight: "auto" }}>As alterações são salvas automaticamente.</span>
        <Button variant="outline-secondary" style={{ borderRadius: 10, fontSize: 13, borderColor: T.hair, color: T.ink2 }} onClick={onHide}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
}

const NEGATIVE_REASONS = [
  "Cancelado pelo Parceiro",
  "Fechou com outra empresa",
  "Já possui o serviço",
  "Mudou de endereço",
  "Não conseguiu contato",
  "Não tem interesse",
  "Outro",
  "Preço acima do esperado",
  "Problemas com documentação",
];
const initialForm = {
  document: "",
  clientName: "",
  phone: "",
  mobile: "",
  city: "",
  address: "",
  state: "",
  condo: "",
  condoId: "",
  unit: "",
  capture: "",
  responsibleName: "",
  responsiblePhone: "",
  notes: "",
  contract: "",
  day: "",
  provider: "",
  plan: "",
  value: "R$ ",
  installationDate: "",
  period: "",
};

const onlyDigits = (v = "") => String(v || "").replace(/\D/g, "");
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";
const dayOptions = Array.from({ length: 30 }, (_, i) => String(i + 1).padStart(2, "0"));
const periodOptions = ["Manhã", "Tarde", "Noite"];
const captureOptions = ["Campanhas", "Indicação Síndico", "Instagram", "Mudança", "Outros"];

function firstPhone(raw) {
  if (!raw) return "";
  const str = String(raw).split(/[;,|]/)[0] || "";
  return formatPhone(str);
}

function isDocValid(doc) {
  const digits = onlyDigits(doc);
  if (!digits) return false;
  return digits.length <= 11 ? cpf.isValid(digits) : cnpj.isValid(digits);
}

const REGISTRATION_TYPE_LABELS = {
  PROPRIETARIO: "Proprietário",
  INQUILINO: "Inquilino",
  MORADOR: "Morador",
  DEPENDENTE: "Dependente",
  SINDICO: "Síndico",
  IMOBILIARIA: "Imobiliária",
  PROCURADOR: "Procurador",
  OUTRO: "Outro",
};
const LEAD_COLUMNS = [
  { key: "novo",       label: "Novos",         color: T.accent, hint: "Arraste para cá para devolver à fila" },
  { key: "tratamento", label: "Em tratamento", color: T.warn,   hint: "Arraste um cadastro para cá para começar" },
];
const LEAD_OUTCOME_LABELS = {
  ATIVADO:    { label: "Ativado",    color: T.ok,     bg: T.okSoft },
  NEGATIVADO: { label: "Negativado", color: T.danger, bg: T.dangerSoft },
};
const DONE_FILTERS = [
  { key: "",           label: "Todos" },
  { key: "ATIVADO",    label: "Ativados" },
  { key: "NEGATIVADO", label: "Negativados" },
];
const DONE_PAGE_SIZE = 25;

const LEAD_HISTORY_LABELS = {
  RECEBIDO:      { text: () => "Cadastro recebido do Eureka (Alterar Titularidade)", color: T.ink3 },
  EM_TRATAMENTO: { text: (who) => `${who} moveu para Em tratamento`,       color: T.warn },
  VOLTOU_NOVO:   { text: (who) => `${who} devolveu para Novos`,            color: T.accent },
  ATIVADO:       { text: (who) => `${who} registrou a ativação`,           color: T.ok },
  NEGATIVADO:    { text: (who) => `${who} registrou uma negativa`,         color: T.danger },
  REABERTO:      { text: (who) => `${who} reabriu o cadastro`,             color: T.accent },
};

// Movimentações do cadastro, mais recentes primeiro
function leadHistory(lead) {
  const history = lead.history?.length
    ? lead.history
    : [{ type: "RECEBIDO", at: lead.createdAt || lead.sourceCreatedAt }];
  return [...history].sort((a, b) => new Date(b.at) - new Date(a.at));
}

function leadStage(lead) {
  if (lead.outcome) return "tratado";
  return lead.inProgressAt ? "tratamento" : "novo";
}

function authorName(email) {
  return (email || "").split("@")[0] || "—";
}

function relTime(date) {
  if (!date) return "—";
  const min = (Date.now() - new Date(date).getTime()) / 60000;
  if (min < 1) return "agora";
  if (min < 60) return `há ${Math.floor(min)} min`;
  const h = min / 60;
  if (h < 24) return `há ${Math.floor(h)} h`;
  const days = Math.floor(h / 24);
  if (days < 30) return `há ${days} dia${days > 1 ? "s" : ""}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `há ${months} ${months > 1 ? "meses" : "mês"}`;
  const years = Math.floor(months / 12);
  return `há ${years} ano${years > 1 ? "s" : ""}`;
}

// Horas decorridas desde `date` contando só segunda a sexta (fins de semana não contam)
function businessHoursSince(date) {
  let cursor = new Date(date).getTime();
  const now = Date.now();
  let ms = 0;
  while (cursor < now) {
    const day = new Date(cursor);
    const nextMidnight = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1).getTime();
    const end = Math.min(nextMidnight, now);
    if (day.getDay() !== 0 && day.getDay() !== 6) ms += end - cursor;
    cursor = end;
  }
  return ms / 3600000;
}

function formatBusinessTime(hours) {
  if (hours < 1) return "menos de 1 h útil";
  if (hours < 24) return `${Math.floor(hours)} h úteis`;
  const days = Math.floor(hours / 24);
  return `${days} dia${days > 1 ? "s" : ""} út${days > 1 ? "eis" : "il"}`;
}

// Tempo parado na etapa atual, em dias úteis: verde < 1, âmbar < 3, vermelho depois disso
function leadAging(lead) {
  const since = lead.inProgressAt || lead.createdAt || lead.sourceCreatedAt;
  const hours = businessHoursSince(since);
  const color = hours < 24 ? T.ok : hours < 72 ? T.warn : T.danger;
  const label = `${lead.inProgressAt ? "Em tratamento há" : "Aguardando há"} ${formatBusinessTime(hours)}`;
  return { color, label };
}

function formatDoc(digits = "") {
  if (digits.length === 11) return cpf.format(digits);
  if (digits.length === 14) return cnpj.format(digits);
  return digits;
}

function leadMapsUrl(lead) {
  const query = [lead.condoName, lead.condoAddress, lead.condoCity].filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function whatsappUrl(phone = "") {
  const digits = onlyDigits(phone);
  return `https://wa.me/${digits.length <= 11 ? `55${digits}` : digits}`;
}

function matchesLead(lead, query) {
  if (!query) return true;
  const q = normName(query);
  const digits = onlyDigits(query);
  return normName(`${lead.fullName} ${lead.condoName || ""} ${lead.unit || ""}`).includes(q)
    || (digits.length >= 3 && (lead.document.includes(digits) || (lead.phone || "").includes(digits)));
}

const stopClick = (e) => e.stopPropagation();
const leadBtn = { display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: T.font, whiteSpace: "nowrap" };
const chipStyle = { display: "inline-flex", alignItems: "center", gap: 4, padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 500, whiteSpace: "nowrap" };

function WhatsAppIcon() {
  return <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true"><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.07c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35zM12.04 21.5h-.01a9.45 9.45 0 0 1-4.82-1.32l-.35-.21-3.58.94.96-3.49-.23-.36a9.43 9.43 0 0 1-1.45-5.03c0-5.22 4.25-9.47 9.48-9.47a9.4 9.4 0 0 1 6.7 2.78 9.41 9.41 0 0 1 2.77 6.7c0 5.22-4.25 9.46-9.47 9.46zm8.06-17.53A11.32 11.32 0 0 0 12.04.63C5.76.63.65 5.74.65 12.02c0 2.01.52 3.97 1.52 5.69L.55 23.6l6.03-1.58a11.36 11.36 0 0 0 5.45 1.39h.01c6.28 0 11.39-5.11 11.39-11.39 0-3.04-1.19-5.9-3.33-8.05z"/></svg>;
}

function LeadAvatar({ name, size = 34 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.3, flexShrink: 0, background: avatarColor(name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.36, fontWeight: 600 }}>
      {initials(name)}
    </div>
  );
}

function LeadCondo({ lead }) {
  if (!lead.condoName) return <span style={{ fontSize: 12.5, color: T.ink4 }}>Condomínio não informado</span>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
      <a href={leadMapsUrl(lead)} target="_blank" rel="noopener noreferrer" title="Abrir no Google Maps" onClick={stopClick} className="lead-link" style={{ display: "inline-flex", alignItems: "center", gap: 5, minWidth: 0, color: T.ink2, fontSize: 12.5, fontWeight: 500, textDecoration: "none" }}>
        <BuildingOffice2Icon style={{ width: 14, height: 14, flexShrink: 0, color: T.ink3 }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.condoName}</span>
      </a>
      {lead.unit && <span style={{ ...chipStyle, background: T.surface2, color: T.ink2, border: `1px solid ${T.hair}` }}>Unid. {lead.unit}</span>}
    </div>
  );
}

function LeadContact({ lead, onCopy }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
      {lead.phone ? (
        <a href={whatsappUrl(lead.phone)} target="_blank" rel="noopener noreferrer" title="Conversar no WhatsApp" onClick={stopClick} className="lead-wa" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: T.okSoft, color: T.ok, fontSize: 12, fontWeight: 500, textDecoration: "none", fontVariantNumeric: "tabular-nums" }}>
          <WhatsAppIcon />{formatPhone(lead.phone)}
        </a>
      ) : <span style={{ fontSize: 12, color: T.ink4 }}>Sem telefone</span>}
      <button type="button" title="Copiar documento" onClick={(e) => { stopClick(e); onCopy(lead); }} className="lead-copy" style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, fontSize: 12, color: T.ink3, cursor: "pointer", fontFamily: T.font, fontVariantNumeric: "tabular-nums" }}>
        {formatDoc(lead.document)}
        <DocumentDuplicateIcon style={{ width: 13, height: 13 }} />
      </button>
    </div>
  );
}

function LeadIconAction({ title, onClick, href, tone, children }) {
  const style = { width: 30, height: 30, borderRadius: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "none", cursor: "pointer", background: tone.bg, color: tone.fg, flexShrink: 0 };
  const handle = (e) => { stopClick(e); onClick?.(); };
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" title={title} aria-label={title} onClick={stopClick} className="lead-icon-btn" style={style}>{children}</a>
  ) : (
    <button type="button" title={title} aria-label={title} onClick={handle} className="lead-icon-btn" style={style}>{children}</button>
  );
}

function LeadCard({ lead, dragging, onOpen, onStart, onDetails, onDragStart, onDragEnd }) {
  const stage = leadStage(lead);
  const aging = leadAging(lead);
  const comments = lead.comments || [];
  const lastComment = comments[comments.length - 1];
  const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  return (
    <div
      className="lead-card"
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(lead)}
      style={{ position: "relative", background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 12, padding: "10px 12px 10px 15px", cursor: "grab", boxShadow: T.shadowSm, opacity: dragging ? 0.4 : 1, overflow: "hidden" }}
    >
      <span title={aging.label} style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: aging.color }} />

      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ ...ellipsis, fontWeight: 600, fontSize: 13.5, color: T.ink, letterSpacing: "-0.005em" }} title={lead.fullName}>{lead.fullName}</span>
        <span title={aging.label} style={{ fontSize: 11, color: aging.color, fontWeight: 600, whiteSpace: "nowrap" }}>{relTime(lead.createdAt).replace("há ", "")}</span>
      </div>

      <div style={{ ...ellipsis, fontSize: 12, color: T.ink3, marginTop: 2 }} title={[lead.condoName, lead.unit && `Unidade ${lead.unit}`].filter(Boolean).join(" · ")}>
        {lead.condoName || "Condomínio não informado"}
        {lead.unit && <> · <span style={{ color: T.ink2, fontWeight: 500 }}>Unid. {lead.unit}</span></>}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 9 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{ ...chipStyle, background: T.accentSoft, color: T.accent }}>{REGISTRATION_TYPE_LABELS[lead.registrationType] || lead.registrationType}</span>
          {comments.length > 0 && (
            <span title={`${comments.length} comentário${comments.length > 1 ? "s" : ""}`} style={{ ...chipStyle, background: T.surface2, color: T.ink2 }}>
              <ChatBubbleLeftRightIcon style={{ width: 12, height: 12 }} />{comments.length}
            </span>
          )}
          {stage === "tratamento" && (
            <span title={aging.label} style={{ ...chipStyle, ...ellipsis, background: T.warnSoft, color: T.warn }}>{authorName(lead.inProgressByEmail)}</span>
          )}
        </div>
        <div style={{ display: "flex", gap: 5 }}>
          {lead.phone && (
            <LeadIconAction title={`WhatsApp · ${formatPhone(lead.phone)}`} href={whatsappUrl(lead.phone)} tone={{ bg: T.okSoft, fg: T.ok }}>
              <WhatsAppIcon />
            </LeadIconAction>
          )}
          <LeadIconAction title="Ver histórico e comentários" onClick={() => onOpen(lead)} tone={{ bg: T.surface2, fg: T.ink2 }}>
            <EyeIcon style={{ width: 15, height: 15 }} />
          </LeadIconAction>
          <LeadIconAction title="Buscar cadastro e ativar" onClick={() => onDetails(lead)} tone={{ bg: T.accentSoft, fg: T.accent }}>
            <RocketLaunchIcon style={{ width: 15, height: 15 }} />
          </LeadIconAction>
          {stage === "novo" && (
            <LeadIconAction title="Mover para Em tratamento" onClick={() => onStart(lead)} tone={{ bg: T.warnSoft, fg: T.warn }}>
              <ArrowRightIcon style={{ width: 15, height: 15 }} />
            </LeadIconAction>
          )}
        </div>
      </div>

      {lastComment && (
        <div title={lastComment.text} style={{ ...ellipsis, fontSize: 11.5, color: T.ink3, marginTop: 8, paddingLeft: 8, borderLeft: `2px solid ${T.hair}` }}>
          <strong style={{ color: T.ink2, fontWeight: 600 }}>{authorName(lastComment.authorEmail)}:</strong> {lastComment.text}
        </div>
      )}
    </div>
  );
}

function LeadPanel({ lead, onHide, onStage, onComment, onDetails, onCopy }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [panelTab, setPanelTab] = useState("comments");
  const listRef = useRef(null);
  const stage = lead ? leadStage(lead) : null;
  const outcome = lead ? LEAD_OUTCOME_LABELS[lead.outcome] : null;

  useEffect(() => { setText(""); setPanelTab("comments"); }, [lead?._id]);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [lead?._id, lead?.comments?.length, panelTab]);

  async function submit(e) {
    e?.preventDefault();
    if (!text.trim() || saving) return;
    setSaving(true);
    if (await onComment(lead, text.trim())) setText("");
    setSaving(false);
  }

  const stageInfo = lead && (
    stage === "tratado"
      ? { label: `${outcome?.label} por ${authorName(lead.outcomeByEmail)} · ${fmtDateTime(lead.outcomeAt)}`, color: outcome?.color, bg: outcome?.bg }
      : stage === "tratamento"
        ? { label: `Em tratamento com ${authorName(lead.inProgressByEmail)} · ${formatBusinessTime(businessHoursSince(lead.inProgressAt))}`, color: T.warn, bg: T.warnSoft }
        : { label: `Novo · ${leadAging(lead).label.toLowerCase()}`, color: T.accent, bg: T.accentSoft }
  );

  return (
    <Offcanvas show={!!lead} onHide={onHide} placement="end" style={{ width: 440, maxWidth: "100vw", fontFamily: T.font }}>
      {lead && (
        <>
          <Offcanvas.Header closeButton style={{ borderBottom: `1px solid ${T.hair}`, alignItems: "flex-start", padding: "18px 20px" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
              <LeadAvatar name={lead.fullName} size={42} />
              <div style={{ minWidth: 0 }}>
                <Offcanvas.Title style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.25 }}>{lead.fullName}</Offcanvas.Title>
                <div style={{ fontSize: 12, color: T.ink3, marginTop: 3 }}>
                  {REGISTRATION_TYPE_LABELS[lead.registrationType] || lead.registrationType} · criado em {fmtDateTime(lead.sourceCreatedAt)} · recebido {relTime(lead.createdAt)}
                </div>
              </div>
            </div>
          </Offcanvas.Header>
          <Offcanvas.Body style={{ display: "flex", flexDirection: "column", gap: 14, padding: 20 }}>
            <span style={{ alignSelf: "flex-start", padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: stageInfo.bg, color: stageInfo.color }}>{stageInfo.label}</span>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, borderRadius: 12, border: `1px solid ${T.hair}`, background: T.surface2 }}>
              <LeadCondo lead={lead} />
              {lead.condoAddress && <div style={{ fontSize: 12, color: T.ink3, marginTop: -4, paddingLeft: 19 }}>{lead.condoAddress}{lead.condoCity ? ` · ${lead.condoCity}` : ""}</div>}
              <LeadContact lead={lead} onCopy={onCopy} />
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {stage !== "tratado" && (
                <button type="button" onClick={() => onDetails(lead)} style={{ ...leadBtn, padding: "8px 14px", background: T.accent, color: "#fff" }}>
                  <RocketLaunchIcon style={{ width: 14, height: 14 }} /> Buscar cadastro e ativar
                </button>
              )}
              {stage === "novo" && (
                <button type="button" onClick={() => onStage(lead, "tratamento")} style={{ ...leadBtn, padding: "8px 14px", background: T.warnSoft, color: T.warn }}>
                  <ArrowRightIcon style={{ width: 14, height: 14 }} /> Mover para Em tratamento
                </button>
              )}
              {stage === "tratamento" && (
                <button type="button" onClick={() => onStage(lead, "novo")} style={{ ...leadBtn, padding: "8px 14px", background: T.surface2, color: T.ink2, border: `1px solid ${T.hair}` }}>
                  <ArrowUturnLeftIcon style={{ width: 13, height: 13 }} /> Voltar para Novos
                </button>
              )}
            </div>

            <div style={{ display: "flex", gap: 18, borderBottom: `1px solid ${T.hair}`, marginTop: 4 }}>
              {[
                { key: "comments", label: "Comentários", count: (lead.comments || []).length, icon: ChatBubbleLeftRightIcon },
                { key: "history",  label: "Histórico",   count: leadHistory(lead).length,     icon: ClockIcon },
              ].map(({ key, label, count, icon: Icon }) => (
                <button key={key} type="button" onClick={() => setPanelTab(key)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 0", marginBottom: -1, background: "none", border: "none", borderBottom: `2px solid ${panelTab === key ? T.accent : "transparent"}`, color: panelTab === key ? T.ink : T.ink3, fontSize: 13, fontWeight: 600, fontFamily: T.font, cursor: "pointer" }}>
                  <Icon style={{ width: 15, height: 15 }} />{label}
                  <span style={{ fontSize: 11, padding: "0 6px", borderRadius: 999, background: panelTab === key ? T.accentSoft : T.surface2, color: panelTab === key ? T.accent : T.ink3 }}>{count}</span>
                </button>
              ))}
            </div>

            {panelTab === "comments" ? (
              <>
                <div ref={listRef} style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, minHeight: 80 }}>
                  {(lead.comments || []).length === 0 && (
                    <div style={{ fontSize: 12.5, color: T.ink4, textAlign: "center", padding: "18px 0" }}>Nenhum comentário ainda. Registre aqui o andamento do contato.</div>
                  )}
                  {(lead.comments || []).map((c) => (
                    <div key={c._id} style={{ display: "flex", gap: 10 }}>
                      <LeadAvatar name={authorName(c.authorEmail)} size={26} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11.5, color: T.ink3, marginBottom: 3 }}>
                          <strong style={{ color: T.ink2, fontWeight: 600 }}>{authorName(c.authorEmail)}</strong> · <span title={fmtDateTime(c.createdAt)}>{relTime(c.createdAt)}</span>
                        </div>
                        <div style={{ fontSize: 13, color: T.ink, whiteSpace: "pre-wrap", wordBreak: "break-word", background: T.surface2, borderRadius: "4px 12px 12px 12px", padding: "8px 12px" }}>{c.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: `1px solid ${T.hair}`, paddingTop: 12 }}>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    autoFocus
                    placeholder="Escreva um comentário interno…"
                    value={text}
                    maxLength={2000}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e); }}
                    style={inputStyle}
                  />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: T.ink4 }}>Ctrl/⌘ + Enter para enviar</span>
                    <button type="submit" disabled={!text.trim() || saving} style={{ ...leadBtn, padding: "8px 16px", background: T.accent, color: "#fff", opacity: !text.trim() || saving ? 0.5 : 1 }}>
                      {saving ? <Spinner size="sm" animation="border" /> : <><PaperAirplaneIcon style={{ width: 14, height: 14 }} /> Comentar</>}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div style={{ flex: 1, overflowY: "auto", minHeight: 80, paddingTop: 4 }}>
                {leadHistory(lead).map((h, i, list) => (
                  <div key={i} style={{ display: "flex", gap: 12 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 12, flexShrink: 0 }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", marginTop: 4, background: LEAD_HISTORY_LABELS[h.type]?.color || T.ink4 }} />
                      {i < list.length - 1 && <span style={{ flex: 1, width: 2, background: T.hair, margin: "3px 0" }} />}
                    </div>
                    <div style={{ paddingBottom: 16, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: T.ink }}>{(LEAD_HISTORY_LABELS[h.type]?.text || (() => h.type))(authorName(h.byEmail))}</div>
                      <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2 }}>{fmtDateTime(h.at)} · {relTime(h.at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Offcanvas.Body>
        </>
      )}
    </Offcanvas>
  );
}

function DoneLeadsTable({ items, loading, onOpen }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="prv-tbl" style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
        <thead>
          <tr>
            <th style={{ cursor: "default" }}>Tratado em</th>
            <th style={{ cursor: "default" }}>Cadastro</th>
            <th style={{ cursor: "default" }}>Condomínio</th>
            <th style={{ cursor: "default" }}>Resultado</th>
            <th style={{ cursor: "default" }}>Responsável</th>
            <th style={{ cursor: "default", textAlign: "right" }}>Comentários</th>
          </tr>
        </thead>
        <tbody>
          {!loading && items.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: "center", color: T.ink3, padding: 32 }}>Nenhum cadastro tratado encontrado.</td></tr>
          )}
          {items.map((lead) => {
            const outcome = LEAD_OUTCOME_LABELS[lead.outcome];
            return (
              <tr key={lead._id} onClick={() => onOpen(lead)} style={{ cursor: "pointer" }}>
                <td style={{ fontSize: 12, color: T.ink2, whiteSpace: "nowrap" }} title={fmtDateTime(lead.outcomeAt)}>{fmtDateTime(lead.outcomeAt)}</td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <LeadAvatar name={lead.fullName} size={28} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500 }}>{lead.fullName}</div>
                      <div style={{ fontSize: 11.5, color: T.ink3, fontVariantNumeric: "tabular-nums" }}>{REGISTRATION_TYPE_LABELS[lead.registrationType] || lead.registrationType} · {formatDoc(lead.document)}</div>
                    </div>
                  </div>
                </td>
                <td style={{ color: T.ink2 }}>
                  {lead.condoName || "—"}
                  {lead.unit && <span style={{ color: T.ink3 }}> · Unid. {lead.unit}</span>}
                </td>
                <td>{outcome && <span style={{ ...chipStyle, padding: "3px 10px", fontSize: 11.5, background: outcome.bg, color: outcome.color }}>{outcome.label}</span>}</td>
                <td style={{ color: T.ink2 }}>{authorName(lead.outcomeByEmail)}</td>
                <td style={{ textAlign: "right", color: T.ink3 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><ChatBubbleLeftRightIcon style={{ width: 14, height: 14 }} />{(lead.comments || []).length}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function IncomingLeadsCard({ token, notify, reloadKey, onOpenDetails }) {
  const [tab, setTab] = useState("board");
  const [boardItems, setBoardItems] = useState([]);
  const [counts, setCounts] = useState({ open: 0, done: 0 });
  const [boardQuery, setBoardQuery] = useState("");
  const [done, setDone] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [doneQuery, setDoneQuery] = useState("");
  const [doneOutcome, setDoneOutcome] = useState("");
  const [donePage, setDonePage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [openLead, setOpenLead] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [overColumn, setOverColumn] = useState(null);

  const fetchLeads = useCallback(async (params) => {
    const r = await fetch(`${API_URL}/admin/incoming-leads?${new URLSearchParams(params)}`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await r.json();
    if (!r.ok) throw new Error(data?.message || "Falha ao carregar cadastros");
    setCounts({ open: data.openCount || 0, done: data.doneCount || 0 });
    return data;
  }, [token]);

  const loadBoard = useCallback(async () => {
    if (!API_URL || !token) return;
    setLoading(true);
    setErr("");
    try {
      const data = await fetchLeads({ view: "board" });
      setBoardItems(data.items || []);
    } catch (e) {
      setErr(e?.message || "Falha ao carregar cadastros");
    } finally {
      setLoading(false);
    }
  }, [token, fetchLeads]);

  const loadDone = useCallback(async () => {
    if (!API_URL || !token) return;
    setLoading(true);
    setErr("");
    try {
      const data = await fetchLeads({ view: "done", q: doneQuery.trim(), outcome: doneOutcome, page: donePage, limit: DONE_PAGE_SIZE });
      setDone({ items: data.items || [], total: data.total || 0, page: data.page || 1, pages: data.pages || 1 });
    } catch (e) {
      setErr(e?.message || "Falha ao carregar cadastros");
    } finally {
      setLoading(false);
    }
  }, [token, fetchLeads, doneQuery, doneOutcome, donePage]);

  useEffect(() => { loadBoard(); }, [loadBoard, reloadKey]);
  useEffect(() => {
    if (tab !== "done") return undefined;
    const t = setTimeout(loadDone, 300);
    return () => clearTimeout(t);
  }, [tab, loadDone, reloadKey]);
  useEffect(() => { setDonePage(1); }, [doneQuery, doneOutcome]);

  const columns = useMemo(() => {
    const groups = { novo: [], tratamento: [] };
    boardItems.filter((lead) => matchesLead(lead, boardQuery)).forEach((lead) => groups[leadStage(lead)]?.push(lead));
    return groups;
  }, [boardItems, boardQuery]);

  function replaceLead(lead) {
    setBoardItems((prev) => prev.map((it) => (it._id === lead._id ? lead : it)));
    setDone((prev) => ({ ...prev, items: prev.items.map((it) => (it._id === lead._id ? lead : it)) }));
    setOpenLead((prev) => (prev && prev._id === lead._id ? lead : prev));
  }

  async function leadRequest(lead, path, method, body) {
    try {
      const r = await fetch(`${API_URL}/admin/incoming-leads/${lead._id}/${path}`, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao atualizar cadastro");
      replaceLead(data.lead);
      return true;
    } catch (e) {
      notify("Erro", e?.message || "Falha ao atualizar cadastro", "danger");
      return false;
    }
  }

  async function moveLead(lead, stage) {
    if (leadStage(lead) === stage) return true;
    // Atualização otimista: o cartão muda de coluna na hora; desfaz se a API falhar
    replaceLead({ ...lead, inProgressAt: stage === "tratamento" ? new Date().toISOString() : null, inProgressByEmail: stage === "tratamento" ? lead.inProgressByEmail : null });
    const ok = await leadRequest(lead, "stage", "PATCH", { stage });
    if (!ok) replaceLead(lead);
    return ok;
  }

  const addComment = (lead, text) => leadRequest(lead, "comments", "POST", { text });

  async function startTreatment(lead) {
    if (await moveLead(lead, "tratamento")) setOpenLead((prev) => prev || boardItems.find((it) => it._id === lead._id) || lead);
  }

  function openDetails(lead) {
    setOpenLead(null);
    onOpenDetails(lead);
  }

  async function copyDoc(lead) {
    try {
      await navigator.clipboard.writeText(lead.document);
      notify("Documento copiado", formatDoc(lead.document));
    } catch (e) {
      notify("Erro", "Não foi possível copiar.", "danger");
    }
  }

  function handleDragStart(e, lead) {
    e.dataTransfer.setData("text/plain", lead._id);
    e.dataTransfer.effectAllowed = "move";
    setDraggingId(lead._id);
  }

  function handleDrop(e, stage) {
    e.preventDefault();
    setOverColumn(null);
    const lead = boardItems.find((it) => it._id === e.dataTransfer.getData("text/plain"));
    setDraggingId(null);
    if (lead) moveLead(lead, stage);
  }

  const draggingLead = draggingId ? boardItems.find((it) => it._id === draggingId) : null;
  const searchInput = (value, onChange, placeholder) => (
    <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 380 }}>
      <MagnifyingGlassIcon style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", width: 15, height: 15, color: T.ink3, pointerEvents: "none" }} />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ width: "100%", height: 34, borderRadius: 10, border: `1px solid ${T.hair}`, background: "#fff", fontSize: 13, padding: "0 12px 0 34px", fontFamily: T.font, color: T.ink, outline: "none" }} />
    </div>
  );
  const segmented = (options, value, onChange) => (
    <div style={{ display: "flex", padding: 3, borderRadius: 10, background: T.surface2, border: `1px solid ${T.hair}` }}>
      {options.map((o) => (
        <button key={o.key} type="button" onClick={() => onChange(o.key)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 8, border: "none", fontSize: 12.5, fontWeight: 500, fontFamily: T.font, cursor: "pointer", background: value === o.key ? "#fff" : "transparent", color: value === o.key ? T.ink : T.ink3, boxShadow: value === o.key ? T.shadowSm : "none" }}>
          {o.icon}{o.label}
          {o.count != null && <span style={{ fontSize: 11, fontWeight: 600, padding: "0 6px", borderRadius: 999, background: value === o.key ? T.accentSoft : "transparent", color: value === o.key ? T.accent : T.ink4 }}>{o.count}</span>}
        </button>
      ))}
    </div>
  );

  return (
    <div style={{ ...cardStyle, marginBottom: 14 }}>
      <style>{`
        .lead-card { transition: box-shadow .15s, border-color .15s, transform .15s; }
        .lead-card:hover { border-color: rgba(30,58,138,.22) !important; box-shadow: ${T.shadowMd} !important; transform: translateY(-1px); }
        .lead-card:active { cursor: grabbing !important; }
        .lead-icon-btn { transition: filter .15s, transform .15s; }
        .lead-icon-btn:hover { filter: brightness(.93); transform: scale(1.06); }
        .lead-link:hover span { text-decoration: underline; }
        .lead-wa:hover { filter: brightness(.95); }
        .lead-copy:hover { color: ${T.accent} !important; }
      `}</style>

      <div style={{ ...cardHeadStyle, padding: "16px 20px", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ ...cardTitleStyle, fontSize: 15 }}>
            <InboxIcon style={{ width: 18, height: 18, color: T.accent }} /> Cadastros recebidos
          </h3>
          <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>Integrado ao Eureka: vindos do Alterar Titularidade. Arraste os cartões entre as colunas e clique para comentar.</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {segmented([
            { key: "board", label: "Em aberto", count: counts.open, icon: <Squares2X2Icon style={{ width: 14, height: 14 }} /> },
            { key: "done", label: "Tratados", count: counts.done, icon: <ArchiveBoxIcon style={{ width: 14, height: 14 }} /> },
          ], tab, setTab)}
          <button type="button" title="Atualizar" onClick={tab === "board" ? loadBoard : loadDone} disabled={loading} style={{ width: 34, height: 34, borderRadius: 10, border: `1px solid ${T.hair}`, background: T.surface, color: T.ink2, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            {loading ? <Spinner size="sm" animation="border" /> : <ArrowPathIcon style={{ width: 16, height: 16 }} />}
          </button>
        </div>
      </div>

      {err && <div style={{ margin: "16px 20px 0", padding: "10px 14px", borderRadius: 10, background: T.dangerSoft, color: T.danger, fontSize: 13 }}>{err}</div>}

      {tab === "board" ? (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "14px 20px 0" }}>
            {searchInput(boardQuery, setBoardQuery, "Buscar por nome, CPF, telefone ou condomínio")}
            <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 11.5, color: T.ink3 }}>
              {[{ c: T.ok, l: "< 1 dia útil" }, { c: T.warn, l: "1–3 dias úteis" }, { c: T.danger, l: "> 3 dias úteis" }].map((x) => (
                <span key={x.l} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 3, height: 12, borderRadius: 2, background: x.c }} />{x.l}</span>
              ))}
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(300px, 1fr))", gap: 14, padding: "14px 20px 20px" }}>
              {LEAD_COLUMNS.map((col) => {
                const list = columns[col.key];
                const canDrop = draggingLead && leadStage(draggingLead) !== col.key;
                const isOver = canDrop && overColumn === col.key;
                return (
                  <div
                    key={col.key}
                    onDragOver={(e) => { if (!canDrop) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (overColumn !== col.key) setOverColumn(col.key); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOverColumn(null); }}
                    onDrop={(e) => handleDrop(e, col.key)}
                    style={{ background: isOver ? T.accentSoft : T.surface2, borderRadius: 16, border: `1.5px ${canDrop ? "dashed" : "solid"} ${isOver ? T.accent : canDrop ? T.ink4 : T.hair2}`, display: "flex", flexDirection: "column", minWidth: 0, transition: "background .15s, border-color .15s" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px 10px" }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{col.label}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 600, padding: "0 7px", borderRadius: 999, background: "#fff", color: T.ink2, border: `1px solid ${T.hair}` }}>{list.length}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 10px 12px", maxHeight: 620, overflowY: "auto", minHeight: 120 }}>
                      {canDrop && (
                        <div style={{ fontSize: 12, color: isOver ? T.accent : T.ink3, textAlign: "center", padding: "10px 0", fontWeight: 500 }}>{isOver ? "Solte para mover" : col.hint}</div>
                      )}
                      {list.length === 0 && !canDrop && (
                        <div style={{ fontSize: 12.5, color: T.ink4, textAlign: "center", padding: "28px 8px" }}>
                          {loading ? "Carregando…" : boardQuery ? "Nada encontrado nesta coluna" : col.key === "novo" ? "Nenhum cadastro novo 🎉" : "Ninguém em tratamento agora"}
                        </div>
                      )}
                      {list.map((lead) => (
                        <LeadCard
                          key={lead._id}
                          lead={lead}
                          dragging={draggingId === lead._id}
                          onOpen={setOpenLead}
                          onStart={startTreatment}
                          onDetails={openDetails}
                          onDragStart={handleDragStart}
                          onDragEnd={() => { setDraggingId(null); setOverColumn(null); }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "14px 20px" }}>
            {searchInput(doneQuery, setDoneQuery, "Buscar por nome, CPF, telefone, condomínio ou unidade")}
            {segmented(DONE_FILTERS, doneOutcome, setDoneOutcome)}
          </div>
          <DoneLeadsTable items={done.items} loading={loading} onOpen={setOpenLead} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: `1px solid ${T.hair}`, fontSize: 12.5, color: T.ink3 }}>
            <span>
              {done.total === 0 ? "0 cadastros" : `${(done.page - 1) * DONE_PAGE_SIZE + 1}–${Math.min(done.page * DONE_PAGE_SIZE, done.total)} de ${done.total}`}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button type="button" disabled={done.page <= 1 || loading} onClick={() => setDonePage((p) => p - 1)} style={{ ...leadBtn, background: T.surface2, color: T.ink2, opacity: done.page <= 1 ? 0.4 : 1 }}>
                <ChevronLeftIcon style={{ width: 14, height: 14 }} />
              </button>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{done.page} / {done.pages}</span>
              <button type="button" disabled={done.page >= done.pages || loading} onClick={() => setDonePage((p) => p + 1)} style={{ ...leadBtn, background: T.surface2, color: T.ink2, opacity: done.page >= done.pages ? 0.4 : 1 }}>
                <ChevronRightIcon style={{ width: 14, height: 14 }} />
              </button>
            </div>
          </div>
        </>
      )}

      <LeadPanel lead={openLead} onHide={() => setOpenLead(null)} onStage={moveLead} onComment={addComment} onDetails={openDetails} onCopy={copyDoc} />
    </div>
  );
}

export default function AdminActivations() {
  const token = useMemo(() => localStorage.getItem("token") || "", []);
  const location = useLocation();
  const navigate = useNavigate();

  const [searchDoc, setSearchDoc] = useState("");
  const [history, setHistory] = useState(null);
  const [historyErr, setHistoryErr] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [lookupErr, setLookupErr] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [activeDoc, setActiveDoc] = useState("");
  const [checksLimit, setChecksLimit] = useState(15);
  const [lookupStatus, setLookupStatus] = useState(null);
  const [showActivationForm, setShowActivationForm] = useState(false);
  const activationFormRef = useRef(null);
  const searchCardRef = useRef(null);
  const [leadsReloadKey, setLeadsReloadKey] = useState(0);
  const [pendingLeadActivation, setPendingLeadActivation] = useState(null);
  const [activationSort, setActivationSort] = useState({ key: "createdAt", dir: "desc" });
  const [activationModal, setActivationModal] = useState({ show: false, mode: null, data: null });
  const [pendingActivationId, setPendingActivationId] = useState(null);
  const [activationForm, setActivationForm] = useState({
    contract: "", day: "", activationDate: "", installationDate: "", provider: "", plan: "", value: "R$ ", period: "", notes: "", status: "PENDENTE", capture: "",
  });
  const [toast, setToast] = useState({ show: false, title: "", message: "", variant: "success" });
  const [deleteModal, setDeleteModal] = useState({ show: false, data: null, reason: "" });
  const [negativeModal, setNegativeModal] = useState({ show: false, prop: null, reason: "", notes: "", saving: false });
  const [form, setForm] = useState(initialForm);
  const [formErr, setFormErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [providerOptions, setProviderOptions] = useState([]);
  const [internetCatalog, setInternetCatalog] = useState([]);
  const [internetPartners, setInternetPartners] = useState([]);
  const [condoProviderConfigs, setCondoProviderConfigs] = useState([]);
  const [showProvidersModal, setShowProvidersModal] = useState(false);
  const isSearching = historyLoading || lookupLoading;

  const visibleChecks = useMemo(() => {
    if (!history?.checks) return [];
    return history.checks.slice(0, checksLimit);
  }, [history, checksLimit]);

  const sortedActivations = useMemo(() => {
    if (!history?.activations) return [];
    const arr = [...history.activations];
    const dir = activationSort.dir === "asc" ? 1 : -1;
    const getStatusScore = (act) => (act?.contract && act?.installationDate ? 1 : 0);
    arr.sort((a, b) => {
      let av, bv;
      switch (activationSort.key) {
        case "provider":  av = (a.provider || "").toLowerCase(); bv = (b.provider || "").toLowerCase(); break;
        case "plan":      av = (a.plan || "").toLowerCase();     bv = (b.plan || "").toLowerCase();     break;
        case "value":     av = Number(onlyDigits(a.value || "")); bv = Number(onlyDigits(b.value || "")); break;
        case "contract":  av = (a.contract || "").toLowerCase(); bv = (b.contract || "").toLowerCase(); break;
        case "status":    av = getStatusScore(a); bv = getStatusScore(b); break;
        case "updatedAt": av = new Date(a.updatedAt || a.createdAt || 0).getTime(); bv = new Date(b.updatedAt || b.createdAt || 0).getTime(); break;
        default:          av = new Date(a.createdAt || 0).getTime(); bv = new Date(b.createdAt || 0).getTime(); break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [history?.activations, activationSort]);

  const memberStatusLabel = lookupStatus === null ? "Sem consulta" : lookupStatus ? "Membro encontrado" : "Não encontrado";

  const showToast = (title, message, variant = "success") => setToast({ show: true, title, message, variant });

  const activationStatus = (act) => {
    if (act?.disabledAt) return { label: "Inativado", active: false, variant: "bg-secondary" };
    let status = act.status;
    if (!status) status = act.contract && act.installationDate ? "ATIVADO" : "PENDENTE";
    const active = status === "ATIVADO";
    return { label: active ? "Ativado" : "Pendente", active, variant: active ? "bg-success" : "bg-warning text-dark", value: status };
  };

  const handleSortActivations = (key) => {
    setActivationSort((prev) => prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  const sortIndicator = (key) => {
    if (activationSort.key !== key) return null;
    return activationSort.dir === "asc" ? " ↑" : " ↓";
  };

  useEffect(() => { loadProviders(); loadCondoProviders(); }, []);

  useEffect(() => {
    if (!history?.document) return;
    if (!activeDoc || history.document !== activeDoc) return;
    const firstProperty = Array.isArray(lookup?.properties) ? lookup.properties[0] : null;
    const firstCheck = Array.isArray(history.checks) ? history.checks[0] : null;
    const formattedPhone = firstPhone(firstProperty?.telefone);
    const formattedMobile = firstPhone(firstProperty?.celular) || firstPhone(firstProperty?.telefone);
    setForm((prev) => {
      const resolvedName = lookup?.fullName || firstProperty?.fullName || history.personName || firstCheck?.partialName || prev.clientName || "";
      return {
        ...prev,
        document: activeDoc,
        clientName: resolvedName,
        phone: prev.phone || formattedPhone || "",
        mobile: prev.mobile || formattedMobile || "",
        address: prev.address || firstProperty?.endereco || "",
        condo: prev.condo || firstProperty?.condoName || firstCheck?.condoName || "",
        condoId: prev.condoId || firstProperty?.condoId || "",
        city: prev.city || firstProperty?.cidade || firstCheck?.city || "",
        state: prev.state || firstProperty?.estado || "",
        unit: prev.unit || firstProperty?.unidade || "",
        responsibleName: resolvedName.split(" ")[0] || resolvedName,
        responsiblePhone: formattedMobile || prev.responsiblePhone || "",
      };
    });
  }, [history, lookup, activeDoc]);

  async function loadProviders() {
    if (!API_URL || !token) return;
    try {
      const r = await fetch(`${API_URL}/admin/partners?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message);
      const names = (data?.items || []).filter((p) => (p.role || "").toLowerCase() !== "admin").map((p) => p.company).filter(Boolean).sort((a, b) => a.localeCompare(b));
      setProviderOptions(Array.from(new Set(names)));
    } catch (e) {}
  }

  const loadCondoProviders = useCallback(async () => {
    if (!API_URL || !token) return;
    try {
      const r = await fetch(`${API_URL}/admin/condo-providers`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message);
      setInternetCatalog(data.providers || []);
      setInternetPartners(data.partners || []);
      setCondoProviderConfigs(data.configs || []);
    } catch (e) {}
  }, [token]);

  function handleCondoConfigChange(change) {
    setCondoProviderConfigs((prev) => {
      const rest = prev.filter((c) => String(c.condoId) !== String(change.condoId));
      return change.removed ? rest : [...rest, change];
    });
  }

  const fetchHistory = useCallback(async (docDigits) => {
    if (!docDigits) return;
    setHistoryLoading(true);
    try {
      const r = await fetch(`${API_URL}/admin/activations/history/${docDigits}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao carregar histórico");
      setHistory(data);
    } catch (e) {
      setHistory(null);
      setHistoryErr(e?.message || "Falha ao carregar histórico");
    } finally {
      setHistoryLoading(false);
    }
  }, [token]);

  const fetchLookup = useCallback(async (docDigits) => {
    if (!docDigits) return;
    setLookupLoading(true);
    try {
      const r = await fetch(`${API_URL}/admin/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ document: docDigits }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao buscar dados completos.");
      const payload = { fullName: data?.fullName || null, document: data?.document || docDigits, properties: Array.isArray(data?.properties) ? data.properties : [] };
      setLookup(payload);
      setLookupStatus(Array.isArray(payload.properties) && payload.properties.length > 0);
      setLookupErr("");
    } catch (e) {
      setLookup(null);
      setLookupStatus(null);
      setLookupErr(e?.message || "Falha ao carregar dados completos.");
    } finally {
      setLookupLoading(false);
    }
  }, [token]);

  const startLookup = useCallback((rawDoc) => {
    if (!rawDoc || !isDocValid(rawDoc)) { setHistoryErr("Informe um CPF/CNPJ válido."); return false; }
    const digits = onlyDigits(rawDoc);
    setSearchDoc(rawDoc);
    setHistory(null);
    setLookup(null);
    setHistoryErr("");
    setLookupErr("");
    setActiveDoc(digits);
    setChecksLimit(15);
    setLookupStatus(null);
    setShowActivationForm(false);
    setForm((prev) => ({ ...prev, document: digits, clientName: "", phone: "", mobile: "", condo: "", condoId: "", city: "", state: "", unit: "", responsibleName: "", responsiblePhone: "", value: "R$ " }));
    fetchHistory(digits);
    fetchLookup(digits);
    return true;
  }, [fetchHistory, fetchLookup]);

  function handleSearch(e) { e.preventDefault(); startLookup(searchDoc); }

  useEffect(() => {
    const state = location.state;
    if (!state || (!state.document && !state.activationId)) return;
    if (state.document) startLookup(state.document);
    if (state.activationId) setPendingActivationId(state.activationId);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate, startLookup]);

  function handleFormChange(field) {
    return (e) => {
      const value = e?.target?.value ?? "";
      const needsFormat = field === "phone" || field === "mobile" || field === "responsiblePhone";
      let normalized = needsFormat ? formatPhone(value) : value;
      if (field === "value") {
        const digits = onlyDigits(normalized);
        if (!digits) { normalized = "R$ "; }
        else {
          const cents = digits.padStart(3, "0");
          normalized = `R$ ${Number(cents.slice(0, -2)).toLocaleString("pt-BR")},${cents.slice(-2)}`;
        }
      }
      setForm((prev) => {
        const next = { ...prev, [field]: normalized };
        if (field === "mobile" && normalized && !prev.responsiblePhone) next.responsiblePhone = normalized;
        return next;
      });
    };
  }

  function applyFromCheck(entry = {}) {
    setForm((prev) => ({ ...prev, document: entry.document || prev.document || history?.document || "", clientName: lookup?.fullName || entry.fullName || entry.partialName || history?.personName || prev.clientName || "", condo: entry.condoName || prev.condo, city: entry.city || prev.city, state: entry.state || prev.state }));
  }

  function applyFromActivation(entry = {}) {
    setForm((prev) => ({ ...prev, document: entry.document || prev.document || history?.document || "", clientName: entry.clientName || prev.clientName || history?.personName || "", condo: entry.condo || prev.condo, city: entry.city || prev.city, plan: entry.plan || prev.plan, value: entry.value || prev.value, provider: entry.provider || prev.provider, capture: entry.capture || prev.capture, responsibleName: entry.leadOwner || prev.responsibleName, responsiblePhone: entry.responsiblePhone || prev.responsiblePhone, installationDate: entry.installationDate || prev.installationDate, period: entry.period || prev.period, unit: entry.unit || prev.unit }));
  }

  const fillActivationForm = useCallback((record) => {
    const derivedStatus = record.status || (record.contract && record.installationDate ? "ATIVADO" : "PENDENTE");
    setActivationForm({ contract: record.contract || "", day: record.day || "", activationDate: record.activationDate || "", installationDate: record.installationDate || "", provider: record.provider || "", plan: record.plan || "", value: record.value || "R$ ", period: record.period || "", notes: record.notes || "", status: derivedStatus, capture: record.capture || "" });
  }, []);

  function openActivationModal(mode, record) {
    if (mode === "edit") fillActivationForm(record);
    setActivationModal({ show: true, mode, data: record });
  }

  useEffect(() => {
    if (!pendingActivationId || !history?.activations?.length) return;
    const target = history.activations.find((act) => act._id === pendingActivationId);
    if (target) { fillActivationForm(target); setActivationModal({ show: true, mode: "edit", data: target }); setPendingActivationId(null); }
  }, [pendingActivationId, history?.activations, fillActivationForm]);

  function closeActivationModal() { setActivationModal({ show: false, mode: null, data: null }); }

  function handleActivationFormChange(field) {
    return (e) => {
      let value = e?.target?.value ?? "";
      if (field === "value") {
        const digits = onlyDigits(value);
        if (!digits) { value = "R$ "; }
        else {
          const cents = digits.padStart(3, "0");
          value = `R$ ${Number(cents.slice(0, -2)).toLocaleString("pt-BR")},${cents.slice(-2)}`;
        }
      }
      setActivationForm((prev) => ({ ...prev, [field]: value }));
    };
  }

  async function handleSaveActivationModal() {
    if (!activationModal.data) return;
    try {
      const r = await fetch(`${API_URL}/admin/activations/${activationModal.data._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contract: activationForm.contract, day: activationForm.day, activationDate: activationForm.activationDate, installationDate: activationForm.installationDate, provider: activationForm.provider, plan: activationForm.plan, value: activationForm.value, period: activationForm.period, dueDate: activationForm.day, notes: activationForm.notes, status: activationForm.status, capture: activationForm.capture }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao atualizar");
      const updated = data.activation;
      setHistory((prev) => {
        if (!prev) return prev;
        return { ...prev, activations: (Array.isArray(prev.activations) ? prev.activations : []).map((act) => String(act._id) === String(updated._id) ? updated : act) };
      });
      showToast("Ativação atualizada", "Dados alterados com sucesso.");
      closeActivationModal();
    } catch (err) {
      showToast("Erro", err?.message || "Falha ao atualizar ativação", "danger");
    }
  }

  async function handleDeleteActivation(record) {
    if (!record) return;
    try {
      const r = await fetch(`${API_URL}/admin/activations/${record._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ reason: deleteModal.reason }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.message || "Falha ao excluir");
      const updated = data.activation;
      setHistory((prev) => {
        if (!prev) return prev;
        return { ...prev, activations: (Array.isArray(prev.activations) ? prev.activations : []).map((act) => String(act._id) === String(record._id) ? updated || { ...act, disabledAt: new Date().toISOString() } : act) };
      });
      showToast("Ativação inativada", "Registro marcado como inativo.", "info");
      setDeleteModal({ show: false, data: null, reason: "" });
    } catch (err) {
      showToast("Erro", err?.message || "Falha ao excluir ativação", "danger");
    }
  }

  async function handleStatusChange(item) {
    if (!item) return;
    const current = activationStatus(item);
    if (current.label === "Inativado") return;
    const newStatus = current.value === "ATIVADO" ? "PENDENTE" : "ATIVADO";
    try {
      const r = await fetch(`${API_URL}/admin/activations/${item._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao atualizar status");
      const updated = data.activation;
      setHistory((prev) => {
        if (!prev) return prev;
        return { ...prev, activations: (Array.isArray(prev.activations) ? prev.activations : []).map((act) => String(act._id) === String(updated._id) ? updated : act) };
      });
      showToast("Status atualizado", `Ativação alterada para ${newStatus === "ATIVADO" ? "Ativado" : "Pendente"}.`);
    } catch (err) {
      showToast("Erro", err?.message || "Falha ao atualizar status", "danger");
    }
  }

  function applyProperty(prop = {}) {
    const formattedPhone = firstPhone(prop.telefone);
    const formattedMobile = firstPhone(prop.celular);
    const nameFromProp = prop.fullName || prop.linkedName || "";
    setForm((prev) => ({
      ...prev,
      document: lookup?.document || history?.document || prop.cpf || prop.document || prev.document || "",
      clientName: prop.fullName || lookup?.fullName || history?.personName || prev.clientName || "",
      condo: prop.condoName || prev.condo,
      condoId: prop.condoId || prev.condoId || "",
      address: prop.endereco || prev.address,
      city: prop.cidade || prev.city,
      state: prop.estado || prev.state,
      unit: prop.unidade || prev.unit,
      phone: formattedPhone || prev.phone,
      mobile: formattedMobile || prev.mobile,
      responsibleName: nameFromProp ? nameFromProp.split(" ")[0] : prev.responsibleName,
      responsiblePhone: formattedMobile || prev.responsiblePhone || "",
    }));
  }

  // Detalhes de um cadastro recebido: carrega histórico + endereços e prepara a ativação
  function handleLeadDetails(lead) {
    if (!startLookup(lead.document)) return;
    setPendingLeadActivation(lead);
  }

  // Quando o lookup do cadastro termina: escolhe o imóvel do condomínio informado e abre o formulário
  useEffect(() => {
    const lead = pendingLeadActivation;
    if (!lead || historyLoading || lookupLoading || activeDoc !== lead.document) return;
    setPendingLeadActivation(null);
    const props = lookup?.properties || [];
    const leadCondo = normName(lead.condoName);
    const leadUnit = normName(lead.unit);
    const sameCondo = leadCondo ? props.filter((p) => normName(p.condoName) === leadCondo) : [];
    const prop = (leadUnit && sameCondo.find((p) => normName(p.unidade) === leadUnit)) || sameCondo[0] || props[0];
    if (prop) applyProperty(prop);
    const leadPhone = lead.phone ? formatPhone(lead.phone) : "";
    setForm((prev) => ({
      ...prev,
      clientName: prev.clientName || lead.fullName,
      condo: prev.condo || lead.condoName || "",
      city: prev.city || lead.condoCity || "",
      unit: prev.unit || lead.unit || "",
      mobile: leadPhone || prev.mobile,
      responsibleName: prev.responsibleName || (lead.fullName || "").split(" ")[0],
      responsiblePhone: leadPhone || prev.responsiblePhone,
    }));
    setShowActivationForm(true);
    setTimeout(() => searchCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [pendingLeadActivation, historyLoading, lookupLoading, activeDoc, lookup]);

  async function handleCreate(e) {
    e.preventDefault();
    const digits = onlyDigits(form.document);
    if (!digits) { setFormErr("Documento é obrigatório."); return; }
    setFormErr("");
    setSaving(true);
    try {
      const { state, responsibleName, responsiblePhone, ...rest } = form;
      const payload = { ...rest, document: digits, dueDate: rest.day || "", leadOwner: responsibleName || "", responsiblePhone: responsiblePhone || rest.mobile || "" };
      const r = await fetch(`${API_URL}/admin/activations`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao registrar ativação");
      showToast("Ativação registrada", "Dados salvos com sucesso.");
      setForm((prev) => ({ ...initialForm, document: prev.document, clientName: prev.clientName, phone: prev.phone, mobile: prev.mobile, city: prev.city, state: prev.state, condo: prev.condo, unit: prev.unit, responsibleName: prev.responsibleName, responsiblePhone: prev.responsiblePhone }));
      setHistory((prev) => prev ? { ...prev, totalActivations: (prev.totalActivations || 0) + 1, activations: [data.activation, ...(prev.activations || [])], personName: prev.personName || data.activation?.clientName || "" } : prev);
      setShowActivationForm(false);
      setLeadsReloadKey((k) => k + 1);
    } catch (e) {
      setFormErr(e?.message || "Falha ao registrar ativação");
    } finally {
      setSaving(false);
    }
  }

  // ── JSX ────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: T.font }}>
      <style>{`
        .prv-tbl th { text-align:left; font-weight:600; font-size:11px; color:${T.ink2}; letter-spacing:.04em; text-transform:uppercase; padding:11px 16px; border-bottom:1px solid ${T.hair}; background:transparent; cursor:pointer; white-space:nowrap; }
        .prv-tbl td { padding:13px 16px; vertical-align:middle; border-bottom:1px solid ${T.hair2}; font-size:13px; color:${T.ink}; }
        .prv-tbl tr:last-child td { border-bottom:none; }
        .prv-tbl tr:hover td { background:${T.surface2}; }
        .act-row .act-actions { opacity:0; pointer-events:none; transition:opacity .15s; }
        .act-row:hover .act-actions { opacity:1; pointer-events:auto; }
      `}</style>

      <ToastNotification
        show={toast.show}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        title={toast.title}
        message={toast.message}
        variant={toast.variant}
      />

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: 24, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 600, letterSpacing: "-0.025em", margin: "0 0 4px", color: T.ink }}>Ativações</h1>
          <p style={{ margin: 0, fontSize: 13, color: T.ink3, maxWidth: 640 }}>
            Consulte um CPF/CNPJ que já passou pela verificação, veja o histórico completo de consultas e salve novas ativações com os dados do seu relatório.
          </p>
        </div>
        <button
          type="button"
          title="Provedores de internet por condomínio"
          onClick={() => setShowProvidersModal(true)}
          style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 12, border: `1px solid ${T.hair}`, background: T.surface, color: T.ink2, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: T.shadowSm }}
        >
          <Cog6ToothIcon style={{ width: 20, height: 20 }} />
        </button>
      </div>

      <CondoProvidersModal
        show={showProvidersModal}
        onHide={() => setShowProvidersModal(false)}
        onOpen={loadCondoProviders}
        token={token}
        catalog={internetCatalog}
        partners={internetPartners}
        onCatalogChange={setInternetCatalog}
        onConfigChange={handleCondoConfigChange}
        notify={showToast}
      />

      {/* ── Cadastros recebidos (integração externa) ───────────────── */}
      <IncomingLeadsCard
        token={token}
        notify={showToast}
        reloadKey={leadsReloadKey}
        onOpenDetails={handleLeadDetails}
      />

      {/* ── Search card ─────────────────────────────────────────────── */}
      <div ref={searchCardRef} style={{ ...cardStyle, padding: 20, marginBottom: 14, scrollMarginTop: 80 }}>
        <style>{`
          .srch-input { width:100%; height:44px; border-radius:12px !important; border:1px solid ${T.hair} !important; background:${T.surface2} !important; font-size:14px !important; padding:0 40px 0 42px !important; font-family:${T.font}; color:${T.ink}; outline:none; box-sizing:border-box; transition:border-color .15s,box-shadow .15s; }
          .srch-input:focus { border-color:${T.accent} !important; box-shadow:0 0 0 3px rgba(30,58,138,.12) !important; background:#fff !important; }
          .srch-input::placeholder { color:#b4b3b8; }
          .srch-btn { display:inline-flex; align-items:center; gap:8px; height:44px; padding:0 20px; border-radius:12px; border:none; background:${T.accent}; color:#fff; font-size:13.5px; font-weight:500; font-family:${T.font}; cursor:pointer; white-space:nowrap; flex-shrink:0; transition:opacity .15s; }
          .srch-btn:disabled { opacity:.6; cursor:not-allowed; }
          .srch-btn:hover:not(:disabled) { opacity:.88; }
          .srch-clear { position:absolute; right:12px; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; padding:2px; color:${T.ink3}; display:flex; align-items:center; }
        `}</style>
        <label style={{ fontSize: 11, fontWeight: 500, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 8 }}>CPF / CNPJ</label>
        <form onSubmit={handleSearch} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, position: "relative" }}>
            <MagnifyingGlassIcon style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, color: T.ink3, pointerEvents: "none" }} />
            <input
              className="srch-input"
              value={searchDoc}
              onChange={(e) => setSearchDoc(e.target.value)}
              placeholder="Digite apenas os números"
            />
            {searchDoc && (
              <button type="button" className="srch-clear" onClick={() => setSearchDoc("")}>
                <XCircleIcon style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>
          <button type="submit" className="srch-btn" disabled={isSearching}>
            {isSearching ? <Spinner size="sm" animation="border" /> : <><MagnifyingGlassIcon style={{ width: 15, height: 15 }} /> Buscar histórico</>}
          </button>
        </form>
        {historyErr && (
          <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 10, background: T.dangerSoft, color: T.danger, fontSize: 13 }}>
            {historyErr}
          </div>
        )}
      </div>

      {/* ── Person hero card ─────────────────────────────────────────── */}
      {history && (
        <div style={{ ...cardStyle, marginBottom: 14 }}>
          <div style={{ padding: 24 }}>
            <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, flexShrink: 0, background: T.accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 600, boxShadow: "0 4px 14px rgba(30,58,138,0.30)" }}>
                {initials(lookup?.fullName || history.personName || "?")}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: "-0.02em", color: T.ink }}>
                    {lookup?.fullName || history.personName || "—"}
                  </h2>
                  {lookupStatus !== null && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", fontSize: 11.5, fontWeight: 500, borderRadius: 999, background: lookupStatus ? T.okSoft : T.dangerSoft, color: lookupStatus ? T.ok : T.danger }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: lookupStatus ? T.ok : T.danger, display: "inline-block" }} />
                      {memberStatusLabel}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: T.ink3, fontVariantNumeric: "tabular-nums" }}>
                  CPF · {history.document || activeDoc}
                </div>
              </div>
              <button
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 10, border: "none", background: T.accentSoft, color: T.accent, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: T.font, flexShrink: 0 }}
                onClick={() => {
                  if (lookup?.properties?.length) applyProperty(lookup.properties[0]);
                  setShowActivationForm(true);
                  setTimeout(() => { const el = activationFormRef.current; if (el) { const y = el.getBoundingClientRect().top + window.scrollY - 80; window.scrollTo({ top: y, behavior: "smooth" }); } }, 100);
                }}
              >
                <CheckCircleIcon style={{ width: 15, height: 15 }} /> Nova ativação
              </button>
            </div>

            {/* Stats grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", marginTop: 20, paddingTop: 20, borderTop: `1px solid ${T.hair}` }}>
              {[
                { label: "Ativações salvas",        value: history.totalActivations || 0,  color: T.accent },
                { label: "Consultas registradas",    value: history.totalChecks || 0 },
                { label: "Negativas",                value: history.totalNegatives || 0,    color: (history.totalNegatives || 0) > 0 ? T.danger : T.ok },
                { label: "Última consulta",          value: history.checks?.length ? fmtDateTime(history.checks[0]?.createdAt) : "—", small: true },
                { label: "Última ativação",          value: history.activations?.length ? fmtDateTime(history.activations[0]?.createdAt) : "—", small: true },
              ].map((s, i) => (
                <div key={i} style={{ padding: "0 16px", borderLeft: i === 0 ? "none" : `1px solid ${T.hair}` }}>
                  <div style={{ fontSize: 11, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 500 }}>{s.label}</div>
                  <div style={{ fontSize: s.small ? 13 : 24, fontWeight: 600, letterSpacing: s.small ? 0 : "-0.02em", marginTop: 4, color: s.color || T.ink, fontVariantNumeric: "tabular-nums" }}>
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Lookup error ─────────────────────────────────────────────── */}
      {lookupErr && (
        <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: T.warnSoft, color: T.warn, fontSize: 13 }}>
          {lookupErr}
        </div>
      )}

      {/* ── Addresses / Properties ───────────────────────────────────── */}
      {lookup?.properties?.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 14 }}>
          <div style={cardHeadStyle}>
            <h3 style={cardTitleStyle}>
              Endereços encontrados
              <span style={{ fontWeight: 400, color: T.ink3 }}>· {lookup.properties.length}</span>
            </h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="prv-tbl" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th>Condomínio</th><th>Unidade</th><th>Nome</th><th>Cidade/UF</th><th>Perfil</th><th>Internet</th>
                <th style={{ textAlign: "right", paddingRight: 20 }}>Ação</th>
              </tr></thead>
              <tbody>
                {lookup.properties.map((prop, idx) => (
                  <tr key={`${prop.cpf || "no-cpf"}-${idx}`}>
                    <td style={{ fontWeight: 500 }}>{prop.condoName || "—"}</td>
                    <td style={{ color: T.ink2 }}>{prop.unidade || "—"}</td>
                    <td style={{ color: T.ink2 }}>{prop.fullName || prop.linkedName || "—"}</td>
                    <td style={{ color: T.ink2 }}>{prop.cidade || "—"}{prop.estado ? `/${prop.estado}` : ""}</td>
                    <td>
                      <span style={{ padding: "3px 10px", borderRadius: 999, background: T.accentSoft, color: T.accent, fontSize: 11.5, fontWeight: 500 }}>
                        {prop.role || prop.linkedRole || "—"}
                      </span>
                    </td>
                    <td>
                      <InternetTags catalog={internetCatalog} config={findCondoProviderConfig(condoProviderConfigs, prop)} />
                    </td>
                    <td style={{ textAlign: "right", paddingRight: 20 }}>
                      <div style={{ display: "inline-flex", gap: 6 }}>
                        <button
                          style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 8, border: "none", background: T.okSoft, color: T.ok, fontSize: 12.5, fontWeight: 500, cursor: "pointer", fontFamily: T.font }}
                          onClick={() => {
                            applyProperty(prop);
                            setShowActivationForm(true);
                            setTimeout(() => { const el = activationFormRef.current; if (el) { const y = el.getBoundingClientRect().top + window.scrollY - 80; window.scrollTo({ top: y, behavior: "smooth" }); } }, 100);
                          }}
                        >
                          <CheckCircleIcon style={{ width: 14, height: 14 }} /> Ativar
                        </button>
                        <button
                          style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 8, border: `1px solid ${T.dangerSoft}`, background: T.dangerSoft, color: T.danger, fontSize: 12.5, fontWeight: 500, cursor: "pointer", fontFamily: T.font }}
                          onClick={() => setNegativeModal({ show: true, prop, reason: "", notes: "", saving: false })}
                        >
                          <XCircleIcon style={{ width: 14, height: 14 }} /> Negativar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modal: view details ───────────────────────────────────────── */}
      <Modal show={activationModal.show && activationModal.mode === "view"} onHide={closeActivationModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em" }}>Detalhes da ativação</span>
            {activationModal.data && <StatusBadge act={activationModal.data} />}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activationModal.data ? (
            <ActivationDetails data={activationModal.data} fmtDateTime={fmtDateTime} clientNameFallback={lookup?.fullName || history?.personName || ""} documentFallback={history?.document} phoneFallback={form.phone} mobileFallback={form.mobile} />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeActivationModal}>Fechar</Button>
          {activationModal.data && !activationModal.data.disabledAt && (
            <Button variant="dark" onClick={() => {
              const rec = activationModal.data;
              closeActivationModal();
              setTimeout(() => openActivationModal("edit", rec), 80);
            }}>
              Editar ativação
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ── Modal: edit activation ───────────────────────────────────── */}
      <Modal show={activationModal.show && activationModal.mode === "edit"} onHide={closeActivationModal} centered size="lg">
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${T.hair}`, padding: "18px 24px" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: T.accentSoft, color: T.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <PencilSquareIcon style={{ width: 18, height: 18 }} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em", color: T.ink }}>Alterar ativação</div>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 1 }}>
                {activationModal.data?.clientName || activationModal.data?.personName || lookup?.fullName || history?.personName || "—"}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Status atual */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 16px", background: "rgba(20,20,22,0.03)", borderRadius: 12, border: `1px solid ${T.hair}` }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.07em", flexShrink: 0 }}>Status atual</span>
            <div style={{ display: "flex", gap: 6 }}>
              {[{ value: "PENDENTE", label: "Pendente" }, { value: "ATIVADO", label: "Ativado" }, { value: "NEGATIVA", label: "Negativa" }].map(({ value, label }) => {
                const active = activationForm.status === value;
                return (
                  <button key={value} type="button" onClick={() => setActivationForm(prev => ({ ...prev, status: value }))} style={{ border: active ? "none" : `1px solid ${T.hair}`, background: active ? T.ink : "transparent", color: active ? "#fff" : T.ink2, borderRadius: 999, padding: "4px 14px", fontSize: 12.5, fontWeight: active ? 600 : 400, cursor: "pointer", fontFamily: T.font, transition: "all .15s" }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* IDENTIFICAÇÃO */}
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Identificação</div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Data da ativação <span style={{ color: T.danger }}>*</span></Form.Label>
                <Form.Control style={inputStyle} type="date" value={activationForm.activationDate} onChange={handleActivationFormChange("activationDate")} />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Contrato</Form.Label>
                <Form.Control style={inputStyle} value={activationForm.contract} onChange={handleActivationFormChange("contract")} placeholder="Nº do contrato" />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Dia do vencimento</Form.Label>
                <Form.Select style={inputStyle} value={activationForm.day} onChange={handleActivationFormChange("day")}>
                  <option value="">Selecione</option>
                  {dayOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                </Form.Select>
              </Col>
            </Row>
          </div>

          {/* PLANO CONTRATADO */}
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Plano contratado</div>
            <Row className="g-3">
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Provedora <span style={{ color: T.danger }}>*</span></Form.Label>
                <ProviderAutocomplete
                  value={activationForm.provider}
                  onChange={handleActivationFormChange("provider")}
                  options={providerOptions}
                  style={{ ...inputStyle, height: 38, padding: "6px 12px" }}
                />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Plano <span style={{ color: T.danger }}>*</span></Form.Label>
                <Form.Control style={inputStyle} value={activationForm.plan} onChange={handleActivationFormChange("plan")} placeholder="Ex: Internet Fibra 500 MB" />
              </Col>
              <Col md={4}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Valor mensal <span style={{ color: T.danger }}>*</span></Form.Label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: T.ink3, fontWeight: 500, pointerEvents: "none", zIndex: 1 }}>R$</span>
                  <Form.Control style={{ ...inputStyle, paddingLeft: 34 }} value={activationForm.value.replace(/^R\$\s*/, "")} onChange={handleActivationFormChange("value")} placeholder="0,00" />
                </div>
              </Col>
            </Row>
          </div>

          {/* INSTALAÇÃO */}
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Instalação</div>
            <Row className="g-3">
              <Col md={6}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Período</Form.Label>
                <Form.Select style={inputStyle} value={activationForm.period} onChange={handleActivationFormChange("period")}>
                  <option value="">Selecione</option>
                  {periodOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label style={{ fontSize: 10.5, fontWeight: 600, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>Data de instalação</Form.Label>
                <Form.Control style={inputStyle} type="date" value={activationForm.installationDate} onChange={handleActivationFormChange("installationDate")} />
              </Col>
            </Row>
          </div>

          {/* CAPTAÇÃO */}
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Captação</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {["Mudança", "Indicação", "Síndico", "Tela Indoor", "Campanha", "Outros"].map((opt) => {
                const active = activationForm.capture === opt;
                return (
                  <button key={opt} type="button" onClick={() => setActivationForm(prev => ({ ...prev, capture: active ? "" : opt }))} style={{ border: `1px solid ${active ? T.accent : T.hair}`, background: active ? T.accentSoft : "transparent", color: active ? T.accent : T.ink2, borderRadius: 999, padding: "5px 15px", fontSize: 13, fontWeight: active ? 600 : 400, cursor: "pointer", fontFamily: T.font, transition: "all .15s" }}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          {/* NOTAS INTERNAS */}
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Notas internas</div>
            <Form.Control style={{ ...inputStyle, resize: "vertical" }} as="textarea" rows={3} value={activationForm.notes} onChange={handleActivationFormChange("notes")} placeholder="Anotações visíveis apenas para o time interno..." />
          </div>

        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${T.hair}`, padding: "14px 24px", gap: 8 }}>
          <Button variant="outline-secondary" style={{ borderRadius: 10, fontSize: 13, borderColor: T.hair, color: T.ink2 }} onClick={closeActivationModal}>Cancelar</Button>
          <Button style={{ borderRadius: 10, fontSize: 13, fontWeight: 500, background: T.accent, borderColor: T.accent, display: "inline-flex", alignItems: "center", gap: 6 }} onClick={handleSaveActivationModal}>
            <CheckCircleIcon style={{ width: 15, height: 15 }} /> Salvar alterações
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal: delete/disable ────────────────────────────────────── */}
      <Modal show={deleteModal.show} onHide={() => setDeleteModal({ show: false, data: null, reason: "" })} centered>
        <Modal.Header closeButton><Modal.Title>Inativar ativação</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="text-danger fw-semibold">Esta ação não poderá ser desfeita.</p>
          <p>Confirma inativar a ativação de<br /><strong>{deleteModal.data?.provider || "—"} - {deleteModal.data?.plan || "—"}</strong>?</p>
          <Form.Label>Motivo</Form.Label>
          <Form.Control style={inputStyle} as="textarea" rows={2} value={deleteModal.reason} onChange={(e) => setDeleteModal((prev) => ({ ...prev, reason: e.target.value }))} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeleteModal({ show: false, data: null, reason: "" })}>Cancelar</Button>
          <Button variant="danger" onClick={() => handleDeleteActivation(deleteModal.data)}>Inativar</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Activation form ──────────────────────────────────────────── */}
      {showActivationForm && (
        <Form ref={activationFormRef} onSubmit={handleCreate}>
          {formErr && (
            <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: T.dangerSoft, color: T.danger, fontSize: 13 }}>
              {formErr}
            </div>
          )}

          {/* Dados do Cliente */}
          <div style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={cardHeadStyle}>
              <h3 style={cardTitleStyle}>Dados do Cliente</h3>
            </div>
            <div style={{ padding: "18px 20px" }}>
              <div style={{ fontSize: 12, color: T.ink3, marginBottom: 16 }}>
                Os dados abaixo servem apenas para registrar esta ativação e não alteram o cadastro do condomínio.
              </div>
              <Row className="g-3">
                <Col md={6}><Form.Label>Cliente</Form.Label><Form.Control style={inputStyle} value={form.clientName} onChange={handleFormChange("clientName")} /></Col>
                <Col md={6}><Form.Label>CPF/CNPJ *</Form.Label><Form.Control style={inputStyle} value={form.document} onChange={handleFormChange("document")} required /></Col>
              </Row>
              <Row className="g-3 mt-1">
                <Col md={6}><Form.Label>Telefone</Form.Label><Form.Control style={inputStyle} value={form.phone} onChange={handleFormChange("phone")} /></Col>
                <Col md={6}><Form.Label>Celular</Form.Label><Form.Control style={inputStyle} value={form.mobile} onChange={handleFormChange("mobile")} /></Col>
              </Row>
              <Row className="g-3 mt-1">
                <Col md={6}><Form.Label>Condomínio</Form.Label><Form.Control style={inputStyle} value={form.condo} onChange={handleFormChange("condo")} /></Col>
                <Col md={6}><Form.Label>Unidade</Form.Label><Form.Control style={inputStyle} value={form.unit} onChange={handleFormChange("unit")} /></Col>
              </Row>
              <Row className="g-3 mt-1">
                <Col md={12}><Form.Label>Endereço</Form.Label><Form.Control style={inputStyle} as="textarea" rows={2} value={form.address} onChange={handleFormChange("address")} /></Col>
              </Row>
              <Row className="g-3 mt-1">
                <Col md={6}><Form.Label>Cidade</Form.Label><Form.Control style={inputStyle} value={form.city} onChange={handleFormChange("city")} /></Col>
                <Col md={6}><Form.Label>Estado</Form.Label><Form.Control style={inputStyle} value={form.state} onChange={handleFormChange("state")} /></Col>
              </Row>
            </div>
          </div>

          {/* Forma de Captação */}
          <div style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={cardHeadStyle}>
              <h3 style={cardTitleStyle}>Forma de Captação</h3>
            </div>
            <div style={{ padding: "18px 20px" }}>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Captação</Form.Label>
                  <Form.Select style={inputStyle} value={form.capture} onChange={handleFormChange("capture")}>
                    <option value="">Selecione</option>
                    {captureOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </Form.Select>
                </Col>
                <Col md={4}><Form.Label>Responsável</Form.Label><Form.Control style={inputStyle} value={form.responsibleName} onChange={handleFormChange("responsibleName")} placeholder="Ligga, IRED, Equipe interna..." /></Col>
                <Col md={4}><Form.Label>Celular do Responsável</Form.Label><Form.Control style={inputStyle} value={form.responsiblePhone} onChange={handleFormChange("responsiblePhone")} /></Col>
              </Row>
              <Row className="g-3 mt-1">
                <Col><Form.Label>Notas</Form.Label><Form.Control style={inputStyle} as="textarea" rows={2} value={form.notes} onChange={handleFormChange("notes")} /></Col>
              </Row>
            </div>
          </div>

          {/* Dados da Ativação */}
          <div style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={cardHeadStyle}>
              <h3 style={cardTitleStyle}>Dados da Ativação</h3>
            </div>
            <div style={{ padding: "18px 20px" }}>
              <Row className="g-3">
                <Col md={6}><Form.Label>Contrato</Form.Label><Form.Control style={inputStyle} value={form.contract} onChange={handleFormChange("contract")} /></Col>
                <Col md={6}>
                  <Form.Label>Dia do vencimento</Form.Label>
                  <Form.Select style={inputStyle} value={form.day} onChange={handleFormChange("day")}>
                    <option value="">Selecione o dia</option>
                    {dayOptions.map((day) => <option value={day} key={day}>{day}</option>)}
                  </Form.Select>
                </Col>
              </Row>
              <Row className="g-3 mt-1">
                <Col md={4}>
                  <Form.Label>Provedora *</Form.Label>
                  <ProviderAutocomplete
                    value={form.provider}
                    onChange={handleFormChange("provider")}
                    options={providerOptions}
                    style={{ ...inputStyle, height: 38, padding: "6px 12px" }}
                    required
                  />
                </Col>
                <Col md={4}><Form.Label>Plano *</Form.Label><Form.Control style={inputStyle} value={form.plan} onChange={handleFormChange("plan")} required /></Col>
                <Col md={4}>
                  <Form.Label>Valor Mensal *</Form.Label>
                  <Form.Control style={inputStyle} value={form.value} onChange={handleFormChange("value")} onFocus={(e) => { if (!e.target.value) handleFormChange("value")({ target: { value: "R$ " } }); }} required />
                </Col>
              </Row>
              <Row className="g-3 mt-1">
                <Col md={6}><Form.Label>Instalação / Ativação</Form.Label><Form.Control style={inputStyle} type="date" value={form.installationDate} onChange={handleFormChange("installationDate")} /></Col>
                <Col md={6}>
                  <Form.Label>Período</Form.Label>
                  <Form.Select style={inputStyle} value={form.period} onChange={handleFormChange("period")}>
                    <option value="">Selecione</option>
                    {periodOptions.map((p) => <option value={p} key={p}>{p}</option>)}
                  </Form.Select>
                </Col>
              </Row>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
                <Button type="button" variant="outline-secondary" className="rounded-pill px-4" onClick={() => setShowActivationForm(false)}>Cancelar</Button>
                <Button type="submit" variant="dark" className="rounded-pill px-4" disabled={saving}>
                  {saving ? <Spinner size="sm" animation="border" /> : "Registrar"}
                </Button>
              </div>
            </div>
          </div>
        </Form>
      )}

      {/* ── History section ──────────────────────────────────────────── */}
      {history && (
        <>
          {/* Ativações da pessoa */}
          <div style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={cardHeadStyle}>
              <h3 style={cardTitleStyle}>
                Ativações da pessoa
                {(history.negatives?.length || 0) > 0 && (
                  <span style={{ padding: "2px 8px", borderRadius: 999, background: T.dangerSoft, color: T.danger, fontSize: 11, fontWeight: 600 }}>
                    {history.negatives.length} negativa{history.negatives.length !== 1 ? "s" : ""}
                  </span>
                )}
              </h3>
            </div>
            {sortedActivations.length ? (
              <div style={{ overflowX: "auto" }}>
                <table className="prv-tbl" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th onClick={() => handleSortActivations("provider")}>Provedora{sortIndicator("provider")}</th>
                    <th onClick={() => handleSortActivations("plan")}>Plano{sortIndicator("plan")}</th>
                    <th onClick={() => handleSortActivations("value")}>Valor{sortIndicator("value")}</th>
                    <th onClick={() => handleSortActivations("contract")}>Contrato{sortIndicator("contract")}</th>
                    <th>Captação</th>
                    <th onClick={() => handleSortActivations("createdAt")}>Inclusão{sortIndicator("createdAt")}</th>
                    <th onClick={() => handleSortActivations("updatedAt")}>Última alt.{sortIndicator("updatedAt")}</th>
                    <th onClick={() => handleSortActivations("status")}>Status{sortIndicator("status")}</th>
                    <th style={{ width: 60 }}></th>
                  </tr></thead>
                  <tbody>
                    {sortedActivations.map((a) => {
                      const info = activationStatus(a);
                      return (
                        <tr key={a._id || a.createdAt} className="act-row">
                          <td style={{ fontWeight: 500 }}>{a.provider || "—"}</td>
                          <td style={{ color: T.ink2 }}>{a.plan || "—"}</td>
                          <td style={{ fontFamily: "'SF Mono',ui-monospace,Menlo,monospace", fontSize: 12 }}>{a.value || "—"}</td>
                          <td style={{ color: T.ink2 }}>{a.contract || "—"}</td>
                          <td style={{ color: T.ink2 }}>{a.capture || "—"}</td>
                          <td style={{ fontSize: 12, color: T.ink2 }}>{fmtDateTime(a.createdAt)}</td>
                          <td style={{ fontSize: 12, color: T.ink2 }}>{fmtDateTime(a.updatedAt || a.createdAt)}</td>
                          <td>
                            <span
                              style={{ cursor: info.label !== "Inativado" ? "pointer" : "default", userSelect: "none" }}
                              onClick={() => handleStatusChange(a)}
                              title={info.label !== "Inativado" ? "Clique para alterar status" : "Ativação inativada"}
                            >
                              <StatusBadge act={a} />
                            </span>
                          </td>
                          <td>
                            <div className="act-actions" style={{ display: "flex", gap: 0 }}>
                              <button style={{ border: 0, background: "transparent", padding: "0 8px", cursor: "pointer", color: T.ink3 }} onClick={() => openActivationModal("view", a)} title="Ver detalhes"><EyeIcon style={{ width: 15, height: 15 }} /></button>
                              {info.label !== "Inativado" && <>
                                <button style={{ border: 0, background: "transparent", padding: "0 8px", cursor: "pointer", color: T.accent }} onClick={() => openActivationModal("edit", a)} title="Editar"><PencilSquareIcon style={{ width: 15, height: 15 }} /></button>
                                <button style={{ border: 0, background: "transparent", padding: "0 8px", cursor: "pointer", color: T.danger }} onClick={() => setDeleteModal({ show: true, data: a, reason: "" })} title="Inativar"><TrashIcon style={{ width: 15, height: 15 }} /></button>
                              </>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: "32px 20px", fontSize: 13, color: T.ink4 }}>Nenhuma ativação cadastrada ainda.</div>
            )}
          </div>

          {/* Negativas */}
          {history.negatives?.length > 0 && (
            <div style={{ ...cardStyle, marginBottom: 14, borderLeft: `3px solid ${T.danger}` }}>
              <div style={cardHeadStyle}>
                <h3 style={cardTitleStyle}>Negativas registradas</h3>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="prv-tbl" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th>Motivo</th><th>Observações</th><th>Condomínio</th><th>Registrado por</th><th>Data</th><th style={{ width: 50 }}></th>
                  </tr></thead>
                  <tbody>
                    {history.negatives.map((neg) => (
                      <tr key={neg._id}>
                        <td>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", fontSize: 11.5, fontWeight: 500, borderRadius: 999, background: T.dangerSoft, color: T.danger }}>
                            {neg.reason}
                          </span>
                        </td>
                        <td style={{ color: T.ink3 }}>{neg.notes || "—"}</td>
                        <td style={{ color: T.ink2 }}>{neg.condo || "—"}</td>
                        <td style={{ color: T.ink2 }}>{neg.createdByName || neg.createdByEmail || "—"}</td>
                        <td style={{ fontSize: 12, color: T.ink2 }}>{fmtDateTime(neg.createdAt)}</td>
                        <td>
                          <button
                            style={{ border: 0, background: "transparent", padding: "0 8px", cursor: "pointer", color: T.danger }}
                            title="Excluir negativa"
                            onClick={async () => {
                              if (!window.confirm("Deseja excluir esta negativa?")) return;
                              try {
                                const r = await fetch(`${API_URL}/admin/negatives/${neg._id}`, { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
                                if (!r.ok) throw new Error("Falha ao excluir");
                                setHistory((prev) => prev ? { ...prev, totalNegatives: Math.max((prev.totalNegatives || 1) - 1, 0), negatives: (prev.negatives || []).filter((n) => n._id !== neg._id) } : prev);
                                setToast({ show: true, title: "Negativa excluída", message: neg.reason, variant: "info" });
                              } catch (err) {
                                setToast({ show: true, title: "Erro", message: err?.message || "Falha ao excluir", variant: "danger" });
                              }
                            }}
                          >
                            <TrashIcon style={{ width: 14, height: 14 }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Histórico de consultas */}
          <div style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={cardHeadStyle}>
              <h3 style={cardTitleStyle}>
                Histórico de consultas
                <span style={{ fontWeight: 400, color: T.ink3 }}>· {history.checks?.length || 0}</span>
              </h3>
            </div>
            {visibleChecks.length ? (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table className="prv-tbl" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th>Parceiro</th><th>Nome</th><th>Condomínio</th><th>Cidade</th><th>Status</th><th>Data</th>
                    </tr></thead>
                    <tbody>
                      {visibleChecks.map((c) => {
                        const partnerName = c.partnerCompany || (c.partner && c.partner.company) || "—";
                        const personName = c.fullName || c.partialName || "—";
                        return (
                          <tr key={c._id || c.createdAt}>
                            <td style={{ fontWeight: 500 }}>{partnerName}</td>
                            <td style={{ color: T.ink2 }}>{personName}</td>
                            <td style={{ color: T.ink2 }}>{c.condoName || "—"}</td>
                            <td style={{ color: T.ink2 }}>{c.city || "—"}{c.state ? `/${c.state}` : ""}</td>
                            <td>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", fontSize: 11.5, fontWeight: 500, borderRadius: 999, background: c.isClient ? T.okSoft : T.dangerSoft, color: c.isClient ? T.ok : T.danger }}>
                                <span style={{ width: 5, height: 5, borderRadius: "50%", background: c.isClient ? T.ok : T.danger, display: "inline-block" }} />
                                {c.isClient ? "Encontrado" : "Não encontrado"}
                              </span>
                            </td>
                            <td style={{ fontSize: 12, color: T.ink2 }}>{fmtDateTime(c.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {history.checks.length > 15 && (
                  <div style={{ display: "flex", justifyContent: "center", padding: "14px 0 6px", borderTop: `1px solid ${T.hair2}` }}>
                    {checksLimit < Math.min(50, history.checks.length) ? (
                      <button style={{ padding: "7px 20px", borderRadius: 10, border: `1px solid ${T.hair}`, background: T.surface, fontSize: 13, fontWeight: 500, cursor: "pointer", color: T.ink2, fontFamily: T.font }} onClick={() => setChecksLimit(Math.min(50, history.checks.length))}>Ver mais</button>
                    ) : (
                      <button style={{ padding: "7px 20px", borderRadius: 10, border: `1px solid ${T.hair}`, background: T.surface, fontSize: 13, fontWeight: 500, cursor: "pointer", color: T.ink2, fontFamily: T.font }} onClick={() => setChecksLimit(15)}>Ver menos</button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: "32px 20px", fontSize: 13, color: T.ink4 }}>Nenhuma consulta registrada.</div>
            )}
          </div>
        </>
      )}

      {/* ── Modal: negativa ──────────────────────────────────────────── */}
      <Modal show={negativeModal.show} onHide={() => setNegativeModal((m) => ({ ...m, show: false }))} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            <XCircleIcon style={{ width: 22, height: 22, color: T.danger, marginRight: 8, verticalAlign: "text-bottom" }} />
            Registrar Negativa
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {negativeModal.prop && (
            <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: T.surface2, fontSize: 13 }}>
              <strong>{negativeModal.prop.fullName || negativeModal.prop.linkedName || "—"}</strong>
              <span style={{ color: T.ink3, marginLeft: 8 }}>{negativeModal.prop.condoName || ""}</span>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold" style={{ fontSize: 13, marginBottom: 10 }}>Motivo *</Form.Label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {NEGATIVE_REASONS.map((r) => {
                const active = negativeModal.reason === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setNegativeModal((m) => ({ ...m, reason: r }))}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 10, fontSize: 13, fontWeight: active ? 500 : 400, cursor: "pointer", fontFamily: T.font, textAlign: "left", border: `1px solid ${active ? T.danger : T.hair}`, background: active ? T.dangerSoft : T.surface2, color: active ? T.danger : T.ink2, transition: "all 0.12s" }}
                  >
                    <span style={{ width: 14, height: 14, borderRadius: "50%", flexShrink: 0, border: `2px solid ${active ? T.danger : T.ink4}`, background: active ? T.danger : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "all 0.12s" }}>
                      {active && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff", display: "block" }} />}
                    </span>
                    {r}
                  </button>
                );
              })}
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label className="fw-semibold">Observações</Form.Label>
            <Form.Control as="textarea" rows={3} placeholder="Detalhes adicionais sobre a negativa..." value={negativeModal.notes} onChange={(e) => setNegativeModal((m) => ({ ...m, notes: e.target.value }))} style={inputStyle} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="rounded-pill px-4" onClick={() => setNegativeModal((m) => ({ ...m, show: false }))}>Cancelar</Button>
          <Button
            variant="danger"
            className="rounded-pill px-4"
            disabled={!negativeModal.reason || negativeModal.saving}
            onClick={async () => {
              setNegativeModal((m) => ({ ...m, saving: true }));
              try {
                const doc = lookup?.document || history?.document || activeDoc;
                const prop = negativeModal.prop || {};
                const r = await fetch(`${API_URL}/admin/negatives`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
                  body: JSON.stringify({ document: doc, clientName: prop.fullName || prop.linkedName || lookup?.fullName || history?.personName || "", reason: negativeModal.reason, notes: negativeModal.notes, condo: prop.condoName || "", city: prop.cidade || "" }),
                });
                if (!r.ok) { const data = await r.json(); throw new Error(data?.message || "Erro ao salvar"); }
                const saved = await r.json();
                setHistory((prev) => prev && prev.document === doc ? { ...prev, totalNegatives: (prev.totalNegatives || 0) + 1, negatives: [saved, ...(prev.negatives || [])] } : prev);
                setNegativeModal({ show: false, prop: null, reason: "", notes: "", saving: false });
                setLeadsReloadKey((k) => k + 1);
                setToast({ show: true, title: "Negativa registrada", message: `Motivo: ${saved.reason}`, variant: "warning" });
              } catch (err) {
                setToast({ show: true, title: "Erro", message: err?.message || "Falha ao registrar negativa", variant: "danger" });
                setNegativeModal((m) => ({ ...m, saving: false }));
              }
            }}
          >
            {negativeModal.saving ? <Spinner size="sm" animation="border" /> : "Registrar negativa"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
